(() => {
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];

  const searchPanel = qs('[data-aa-search]');
  const mobileNav = qs('[data-aa-mobile-nav]');
  const menuToggle = qs('[data-aa-menu-toggle]');

  function paintSalePrice(el, price, compare) {
    if (!el) return;
    const clean = (value) => String(value ?? '').replace(/[^0-9.]/g, '');
    const now = clean(price);
    const was = clean(compare);
    const onSale = now !== '' && was !== '' && Number(was) > Number(now);
    el.classList.toggle('is-sale', onSale);
    const current = now ? `<span class="aa-price-current">$${now}</span>` : '';
    const struck = onSale ? `<s class="aa-price-compare">$${was}</s>` : '';
    el.innerHTML = `${current}${struck}`;
  }

  window.aaPaintPrice = paintSalePrice;

  function setCartCount(count) {
    qsa('[data-aa-cart-count]').forEach((el) => {
      el.textContent = String(count || 0);
    });
  }

  function cartDrawer() {
    return qs('theme-drawer#cart-drawer');
  }

  function isCartOpen() {
    const drawer = cartDrawer();
    if (!drawer) return false;
    const panel = drawer.querySelector('dialog');
    if (panel) return panel.open === true;
    return drawer.isOpen === true;
  }

  function openCart() {
    if (isCartOpen()) return;
    const drawer = cartDrawer();
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    if (window.Shopify?.actions?.openCart) {
      window.Shopify.actions.openCart();
    }
  }

  function revealCart() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          openCart();
          resolve();
        });
      });
    });
  }

  function fulfillmentOf(variant, product) {
    return variant?.fulfillment || product?.fulfillment || null;
  }

  function lineProperties(variant, product) {
    const info = fulfillmentOf(variant, product);
    if (!info || info.mode !== 'preorder' || info.available === false) return null;
    const props = {};
    // Shopify hides line item properties whose names begin with an underscore
    // from checkout and customer-facing property lists. The cart can still use
    // them as a durable snapshot after an Ajax add and section refresh.
    if (info.purchaseType) props['_Purchase type'] = info.purchaseType;
    if (info.estimatedShipping) props['_Estimated shipping'] = info.estimatedShipping;
    return Object.keys(props).length ? props : null;
  }

  function productDataForButton(button) {
    const json = button?.closest('[data-aa-product]')?.querySelector('[data-aa-product-json]');
    if (!json) return null;
    try {
      return JSON.parse(json.textContent);
    } catch (err) {
      console.warn('Product card data is invalid.', err);
      return null;
    }
  }

  function paintFulfillment(root, info, available) {
    if (!root) return;
    const status = root.querySelector('[data-qv-status] span');
    const block = root.querySelector('[data-qv-preorder]');
    const intro = root.querySelector('[data-qv-preorder-intro]');
    const detail = root.querySelector('[data-qv-preorder-detail]');
    const addLabel = root.querySelector('[data-qv-add-label]');
    const preorder = Boolean(available && info?.mode === 'preorder');
    const made = Boolean(available && info?.mode === 'made_to_order');
    if (status) {
      if (!available) status.textContent = 'OUT OF STOCK';
      else if (info?.statusLabel) status.textContent = info.statusLabel;
      else status.textContent = 'IN STOCK · READY TO SHIP';
    }
    if (block) block.hidden = !preorder;
    if (intro) {
      intro.hidden = !info?.intro;
      intro.textContent = info?.intro || '';
    }
    if (detail) {
      detail.hidden = !info?.detail;
      detail.textContent = info?.detail || '';
    }
    if (addLabel) {
      if (!available) addLabel.textContent = 'SOLD OUT';
      else if (preorder) addLabel.textContent = info.buttonLabel || 'PREORDER';
      else addLabel.textContent = 'ADD TO CART';
    }
    root.querySelector('[data-qv-status]')?.classList.toggle('is-preorder', preorder);
    root.querySelector('[data-qv-status]')?.classList.toggle('is-made', made);
  }

  async function addVariantToCart({ variantId, quantity = 1, live = true, properties = null }) {
    const qty = Math.max(1, Number(quantity) || 1);
    const id = String(variantId || '').replace(/[^\d]/g, '');
    if (!live || !id) return false;

    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const form = new FormData();
    form.set('id', id);
    form.set('quantity', String(qty));
    if (properties && typeof properties === 'object') {
      Object.entries(properties).forEach(([key, value]) => {
        if (value != null && String(value) !== '') form.append(`properties[${key}]`, String(value));
      });
    }
    if (sectionIds.length) form.set('sections', sectionIds.join(','));
    form.set('sections_url', window.location.pathname);

    let CartLinesUpdateEvent = null;
    try {
      ({ CartLinesUpdateEvent } = await import('@shopify/events'));
    } catch (err) {
      CartLinesUpdateEvent = null;
    }

    let deferred = null;
    if (CartLinesUpdateEvent?.createPromise) {
      deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: id, quantity: qty }],
          promise: deferred.promise,
        }),
      );
    }

    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: form,
    });
    const data = await response.json();
    if (data.status) {
      const message = data.description || data.message || 'Add to cart failed';
      deferred?.reject?.(new Error(message));
      throw new Error(message);
    }

    const ajaxCart = await fetch(`${cartUrl}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((res) => res.json());

    if (deferred && CartLinesUpdateEvent?.createCartFromAjaxResponse) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          itemCount: ajaxCart.item_count,
          sections: data.sections,
          source: 'aa-product-card',
          didError: false,
        },
      });
    } else {
      deferred?.resolve?.({ cart: ajaxCart, detail: { items: ajaxCart.items, itemCount: ajaxCart.item_count, sections: data.sections } });
    }

    if (typeof ajaxCart.item_count === 'number') setCartCount(ajaxCart.item_count);
    return 'ajax';
  }

  async function addToBag(button, quantity = 1) {
    const variantId = button.dataset.variantId;
    const live = button.dataset.liveProduct === 'true';
    const product = productDataForButton(button);
    const variant = product?.variants?.find((item) => String(item?.id || '') === String(variantId || '')) || null;
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      const added = await addVariantToCart({
        variantId,
        quantity,
        live,
        properties: lineProperties(variant, product),
      });
      if (added) await revealCart();
      else openCart();
      button.classList.add('is-added');
      window.setTimeout(() => button.classList.remove('is-added'), 1600);
      return true;
    } catch (err) {
      console.warn('Add to bag failed.', err);
      openCart();
      return false;
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  window.aaAddVariantToCart = addVariantToCart;
  window.aaLineProperties = lineProperties;
  window.aaPaintFulfillment = paintFulfillment;
  window.aaOpenCart = openCart;
  window.aaRevealCart = revealCart;

  const qvModal = qs('[data-aa-quick-view-modal]');
  const qvState = { quantity: 1, selectedOptions: {}, product: null, currentVariant: null };

  function setQuickViewOpen(open) {
    if (!qvModal) return;
    qvModal.hidden = !open;
    const filterDrawer = qs('[data-aa-filter-drawer]');
    const drawerOpen = Boolean(filterDrawer && !filterDrawer.hidden);
    document.body.style.overflow = open || drawerOpen ? 'hidden' : '';
    if (open) {
      const scroller = qvModal.querySelector('.shop-modal-scroll');
      if (scroller) scroller.scrollTop = 0;
      requestAnimationFrame(() => {
        const strip = qvModal.querySelector('[data-qv-thumbs]');
        const active = strip?.querySelector('[data-qv-thumb].active');
        if (!strip || strip.hidden || !active) return;
        const left = active.offsetLeft - (strip.clientWidth - active.clientWidth) / 2;
        strip.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
      });
    } else {
      qvState.product = null;
      qvState.currentVariant = null;
      qvState.selectedOptions = {};
      qvState.quantity = 1;
      const qtyEl = qvModal.querySelector('[data-qv-qty]');
      if (qtyEl) qtyEl.textContent = '1';
    }
  }

  function escapeQuickView(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function quickViewImageSrc(variant, product) {
    const own = variant && typeof variant.image === 'string' ? variant.image.trim() : '';
    if (own) return own;
    const featured = product && typeof product.featuredImage === 'string' ? product.featuredImage.trim() : '';
    if (featured) return featured;
    const card = product && typeof product.image === 'string' ? product.image.trim() : '';
    return card;
  }

  function realProductOptions(product) {
    return (product.options || []).filter((option) => {
      const values = option.values || [];
      if (!values.length) return false;
      if (option.name === 'Title' && (values.length === 1 || values[0] === 'Default Title')) return false;
      if (values.length === 1 && values[0] === 'Default Title') return false;
      return true;
    });
  }

  function findQuickViewVariant(product) {
    const variants = product.variants || [];
    const options = realProductOptions(product);
    if (!options.length) return variants[0] || { id: product.variantId, price: product.price, available: product.available };
    return (
      variants.find((variant) =>
        (variant.options || []).every((value, index) => {
          const name = product.options?.[index]?.name;
          return !name || qvState.selectedOptions[name] === value;
        }),
      ) || variants[0]
    );
  }

  function fillQuickView(product) {
    if (!qvModal || !product) return;
    qvState.product = product;
    qvState.quantity = 1;
    const options = realProductOptions(product);
    qvState.selectedOptions = Object.fromEntries(options.map((option) => [option.name, option.values[0] || '']));
    const typeEl = qvModal.querySelector('[data-qv-type]');
    if (typeEl) typeEl.textContent = product.type || '';
    const titleEl = qvModal.querySelector('[data-qv-title]');
    if (titleEl) titleEl.textContent = product.title || '';
    const artist = qvModal.querySelector('[data-qv-artist]');
    const artistName = typeof product.artist === 'string' ? product.artist.trim() : '';
    if (artist) {
      artist.hidden = artistName === '';
      if (artistName) {
        artist.textContent = `BY ${artistName.toUpperCase()} →`;
        artist.href = product.artistUrl || product.url || '#';
      }
    }
    const details = qvModal.querySelector('[data-qv-details]');
    if (details) details.href = product.url || '#';
    qvModal.querySelector('[data-aa-quick-view-dialog]')?.setAttribute('aria-label', product.title || 'Quick view');
    const optionRoot = qvModal.querySelector('[data-qv-options]');
    if (optionRoot) optionRoot.innerHTML = renderQuickViewOptions(options);
    const strip = qvModal.querySelector('[data-qv-thumbs]');
    if (strip) strip.dataset.qvProduct = '';
    const image = qvModal.querySelector('[data-qv-image]');
    if (image) image.dataset.qvProduct = '';
    updateQuickViewVariant();
  }

  function renderQuickViewOptions(options) {
    return options
      .map((option) => {
        const name = escapeQuickView(option.name);
        const values = option.values || [];
        const selected = qvState.selectedOptions[option.name];
        const controls =
          values.length > 8
            ? `<select data-option-name="${name}" aria-label="${name}">${values
                .map((value) => {
                  const safe = escapeQuickView(value);
                  return `<option value="${safe}"${selected === value ? ' selected' : ''}>${safe}</option>`;
                })
                .join('')}</select>`
            : values
                .map((value) => {
                  const safe = escapeQuickView(value);
                  const on = selected === value ? ' active' : '';
                  return `<button type="button" data-option-name="${name}" data-option-value="${safe}" class="${on.trim()}">${safe}</button>`;
                })
                .join('');
        return `<fieldset class="shop-modal-option"><legend>${name}</legend><div>${controls}</div></fieldset>`;
      })
      .join('');
  }

  function syncQuickViewMedia(variant, product) {
    if (!qvModal || !product) return;
    const img = qvModal.querySelector('[data-qv-image]');
    const strip = qvModal.querySelector('[data-qv-thumbs]');
    const next = quickViewImageSrc(variant, product);
    const productKey = String(product.id || product.handle || product.title || '');
    if (img) {
      const sameProduct = img.dataset.qvProduct === productKey && img.dataset.qvProduct !== '';
      const current = img.getAttribute('src') || '';
      const resolved = next || (sameProduct ? current : '');
      img.dataset.qvProduct = productKey;
      img.dataset.qvFeatured = (product.featuredImage || product.image || '').trim();
      img.alt = (variant && variant.imageAlt) || product.title || '';
      if (resolved) {
        if (resolved !== current) {
          img.dataset.qvFailed = '';
          img.src = resolved;
        }
      } else if (!sameProduct) {
        img.removeAttribute('src');
      }
    }
    if (!strip) return;
    const media = Array.isArray(product.media) ? product.media.filter((item) => item && item.src) : [];
    if (media.length < 2) {
      strip.hidden = true;
      strip.innerHTML = '';
      strip.dataset.qvProduct = '';
      return;
    }
    if (strip.dataset.qvProduct !== productKey) {
      strip.dataset.qvProduct = productKey;
      strip.innerHTML = media
        .map((item) => {
          const id = escapeQuickView(item.id);
          const src = escapeQuickView(item.src);
          const alt = escapeQuickView(item.alt || product.title || '');
          return `<button type="button" data-qv-thumb data-media-id="${id}" data-src="${src}" aria-label="${alt}"><img src="${src}" alt=""></button>`;
        })
        .join('');
    }
    strip.hidden = false;
    const mediaId = variant && variant.mediaId != null && variant.mediaId !== '' ? String(variant.mediaId) : '';
    let active = null;
    strip.querySelectorAll('[data-qv-thumb]').forEach((btn) => {
      const on = mediaId ? btn.dataset.mediaId === mediaId : Boolean(next) && btn.dataset.src === next;
      btn.classList.toggle('active', on);
      btn.setAttribute('aria-current', on ? 'true' : 'false');
      if (on && !active) active = btn;
    });
    if (active) {
      const left = active.offsetLeft - (strip.clientWidth - active.clientWidth) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
    }
  }

  window.aaSyncQuickViewMedia = syncQuickViewMedia;

  if (qvModal) {
    const qvImage = qvModal.querySelector('[data-qv-image]');
    qvImage?.addEventListener('error', () => {
      if (!qvImage || qvImage.dataset.qvFailed === '1') return;
      const featured = qvImage.dataset.qvFeatured || '';
      const current = qvImage.getAttribute('src') || '';
      if (featured && current && featured !== current) {
        qvImage.dataset.qvFailed = '1';
        qvImage.src = featured;
      }
    });
  }

  function updateQuickViewVariant() {
    if (!qvModal || !qvState.product) return;
    const variant = findQuickViewVariant(qvState.product);
    const price = variant?.price || qvState.product.price;
    const compare = variant ? variant.compare : qvState.product.compare;
    paintSalePrice(qvModal.querySelector('[data-qv-price]'), price, compare);
    const qtyEl = qvModal.querySelector('[data-qv-qty]');
    if (qtyEl) qtyEl.textContent = String(qvState.quantity);
    const available = variant?.available !== false && qvState.product.available !== false;
    paintFulfillment(qvModal, fulfillmentOf(variant, qvState.product), available);
    const addBtn = qvModal.querySelector('[data-qv-add]');
    if (addBtn) {
      addBtn.disabled = !available;
      if (variant?.id) addBtn.dataset.variantId = String(variant.id);
      else addBtn.removeAttribute('data-variant-id');
    }
    qvState.currentVariant = variant;
    syncQuickViewMedia(variant, qvState.product);
  }

  function applyQuickViewOption(name, value) {
    if (!qvModal || !qvState.product || !name) return;
    qvState.selectedOptions[name] = value;
    qvModal.querySelectorAll(`[data-option-name="${CSS.escape(name)}"]`).forEach((el) => {
      if (el instanceof HTMLSelectElement) {
        if (el.value !== value) el.value = value;
        return;
      }
      el.classList.toggle('active', el.dataset.optionValue === value);
    });
    updateQuickViewVariant();
  }

  document.addEventListener('shopify:cart:lines-update', (event) => {
    event.promise
      ?.then(({ cart }) => {
        if (cart && typeof cart.totalQuantity === 'number') setCartCount(cart.totalQuantity);
      })
      .catch(() => {});
  });

  document.addEventListener('change', (event) => {
    const select = event.target instanceof Element ? event.target.closest('select[data-option-name]') : null;
    if (!select || !qvModal || qvModal.hidden || !select.closest('[data-qv-options]') || !qvState.product) return;
    applyQuickViewOption(select.dataset.optionName, select.value);
  });

  document.addEventListener('click', (event) => {
    const add = event.target.closest('.aa-add-to-bag');
    if (add) {
      event.preventDefault();
      event.stopPropagation();
      if (add.disabled || add.classList.contains('is-sold-out') || add.classList.contains('is-loading')) return;
      if (add.dataset.requiresChoice === 'true') {
        /* Multiple variants: Horizon opens quick add instead of guessing a variant. */
        const card = add.closest('[data-aa-product]');
        const json = card?.querySelector('[data-aa-product-json]');
        const href = card?.querySelector('a')?.getAttribute('href');
        if (json && qvModal) {
          try {
            fillQuickView(JSON.parse(json.textContent));
            setQuickViewOpen(true);
            return;
          } catch (err) {
            console.warn('Variant choice needs the product page.', err);
          }
        }
        if (href) location.assign(href);
        return;
      }
      addToBag(add);
      return;
    }
    const quick = event.target.closest('[data-aa-quick-view]');
    if (quick) {
      event.preventDefault();
      event.stopPropagation();
      const card = quick.closest('[data-aa-product]');
      const json = card?.querySelector('[data-aa-product-json]');
      if (!json || !qvModal) return;
      try {
        fillQuickView(JSON.parse(json.textContent));
        setQuickViewOpen(true);
      } catch (err) {
        console.warn('Quick view data missing.', err);
      }
      return;
    }
    if (qvModal && (event.target.closest('[data-aa-quick-view-close]') || event.target === qvModal)) {
      setQuickViewOpen(false);
      return;
    }
    const qvThumb = event.target.closest('[data-qv-thumb]');
    if (qvThumb && qvModal && !qvModal.hidden && qvThumb.closest('[data-qv-thumbs]') && qvState.product) {
      const mediaId = qvThumb.dataset.mediaId || '';
      const matched = (qvState.product.variants || []).find((item) => item && String(item.mediaId || '') === mediaId && item.image);
      if (matched && Array.isArray(matched.options)) {
        realProductOptions(qvState.product).forEach((option, index) => {
          const value = matched.options[index];
          if (value) qvState.selectedOptions[option.name] = value;
        });
        realProductOptions(qvState.product).forEach((option) => {
          const value = qvState.selectedOptions[option.name];
          qvModal.querySelectorAll(`[data-option-name="${CSS.escape(option.name)}"]`).forEach((el) => {
            if (el instanceof HTMLSelectElement) el.value = value;
            else el.classList.toggle('active', el.dataset.optionValue === value);
          });
        });
        updateQuickViewVariant();
        return;
      }
      const img = qvModal.querySelector('[data-qv-image]');
      if (img && qvThumb.dataset.src) img.src = qvThumb.dataset.src;
      qvModal.querySelectorAll('[data-qv-thumb]').forEach((btn) => {
        const on = btn === qvThumb;
        btn.classList.toggle('active', on);
        btn.setAttribute('aria-current', on ? 'true' : 'false');
      });
      return;
    }
    const optionBtn = event.target.closest('[data-qv-options] button[data-option-name]');
    if (optionBtn && qvModal && !qvModal.hidden && qvState.product) {
      applyQuickViewOption(optionBtn.dataset.optionName, optionBtn.dataset.optionValue);
      return;
    }
    if (event.target.closest('[data-qv-minus]') && qvState.product) {
      qvState.quantity = Math.max(1, qvState.quantity - 1);
      updateQuickViewVariant();
      return;
    }
    if (event.target.closest('[data-qv-plus]') && qvState.product) {
      qvState.quantity += 1;
      updateQuickViewVariant();
      return;
    }
    const qvAdd = event.target.closest('[data-qv-add]');
    if (qvAdd && qvState.product) {
      event.preventDefault();
      const variant = qvState.currentVariant || findQuickViewVariant(qvState.product);
      qvAdd.disabled = true;
      addVariantToCart({
        variantId: variant?.id || qvState.product.variantId,
        quantity: qvState.quantity,
        live: Boolean(qvState.product.live),
        properties: lineProperties(variant, qvState.product),
      })
        .then((added) => {
          setQuickViewOpen(false);
          if (added) return revealCart();
          openCart();
        })
        .catch((err) => {
          console.warn('Add to cart failed.', err);
          setQuickViewOpen(false);
          openCart();
        })
        .finally(() => {
          qvAdd.disabled = false;
        });
      return;
    }
    if (event.target.closest('[data-aa-search-open]')) {
      event.preventDefault();
      if (searchPanel) searchPanel.hidden = false;
      searchPanel?.querySelector('input')?.focus();
      return;
    }
    if (event.target.closest('[data-aa-search-close]') || event.target === searchPanel) {
      if (searchPanel) searchPanel.hidden = true;
      return;
    }
    const tab = event.target.closest('[data-aa-product-tabs] [data-tab]');
    if (tab) {
      const nav = tab.parentElement;
      qsa('[data-tab]', nav).forEach((btn) => btn.classList.toggle('active', btn === tab));
      const section = tab.closest('.product-section');
      const value = tab.getAttribute('data-tab');
      qsa('[data-aa-product]', section).forEach((card) => {
        card.hidden = value !== 'ALL' && card.dataset.category !== value;
      });
    }
  });

  const menuIcon = `<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 5h16"/><path d="M4 12h16"/><path d="M4 19h16"/></svg>`;
  const closeIcon = `<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;

  function placeMobileNav() {
    const header = document.querySelector('header');
    if (!header) return;
    const viewport = window.visualViewport;
    const viewHeight = viewport ? viewport.height : window.innerHeight;
    const headerBottom = header.getBoundingClientRect().bottom;
    const room = Math.max(160, Math.round(viewHeight - Math.max(0, headerBottom)));
    document.documentElement.style.setProperty('--aa-menu-max', `${room}px`);
  }

  function setMobileNav(open) {
    if (!menuToggle || !mobileNav) return;
    mobileNav.hidden = !open;
    menuToggle.setAttribute('aria-expanded', String(open));
    menuToggle.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
    menuToggle.innerHTML = open ? closeIcon : menuIcon;
    document.body.classList.toggle('aa-nav-open', open);
    document.documentElement.classList.toggle('aa-nav-open', open);
    if (open) {
      placeMobileNav();
      requestAnimationFrame(placeMobileNav);
    }
  }

  if (menuToggle && mobileNav) {
    menuToggle.addEventListener('click', () => {
      setMobileNav(mobileNav.hasAttribute('hidden'));
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !mobileNav.hidden) setMobileNav(false);
    });
    window.addEventListener('resize', () => {
      if (window.innerWidth > 760 && !mobileNav.hidden) setMobileNav(false);
      else placeMobileNav();
    });
    window.visualViewport?.addEventListener('resize', placeMobileNav);
    window.visualViewport?.addEventListener('scroll', placeMobileNav);
    document.addEventListener('touchmove', (event) => {
      if (!document.body.classList.contains('aa-nav-open')) return;
      if (event.target.closest('.mobile-nav, header')) return;
      event.preventDefault();
    }, { passive: false });
    qsa('[data-aa-mobile-item] > button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.parentElement;
        const wasOpen = item.classList.contains('open');
        qsa('[data-aa-mobile-item]').forEach((el) => el.classList.remove('open'));
        if (!wasOpen) item.classList.add('open');
      });
    });
  }
})();

(async () => {
  const shop = document.querySelector('[data-aa-shop]');
  if (!shop) return;

  const qs = (sel, root = shop) => root.querySelector(sel);
  const qsa = (sel, root = shop) => [...(root || shop).querySelectorAll(sel)];
  const live = shop.dataset.aaShopLive === 'true';
  const navMode = shop.dataset.aaNavMode === 'tabs' ? 'tabs' : 'filter_only';
  const pageSize = Number(shop.dataset.aaPageSize || 25);
  const collectionUrl = shop.dataset.aaCollectionUrl || location.pathname;
  const firstTab = (shop.dataset.aaFirstTab || 'ALL').toUpperCase();
  const defaultSort = shop.dataset.aaDefaultSort || 'manual';
  const artistParam = shop.dataset.aaArtistSource === 'metafield'
    ? 'filter.p.m.custom.artist'
    : 'filter.p.vendor';
  const params = () => new URLSearchParams(location.search);
  const SORT_ALIAS = {
    newest: 'created-descending',
    'price-asc': 'price-ascending',
    'price-desc': 'price-descending',
    featured: 'manual',
  };
  const SORT_LABEL = {
    relevance: 'RELEVANCE',
    manual: 'FEATURED',
    'best-selling': 'FEATURED',
    'created-descending': 'NEWEST',
    'created-ascending': 'NEWEST',
    'price-ascending': 'PRICE: LOW TO HIGH',
    'price-descending': 'PRICE: HIGH TO LOW',
  };
  const countWord = shop.dataset.aaCountLabel || 'PRODUCTS';
  function parsePriceBuckets(raw) {
    const map = {};
    const text = String(raw || '')
      .replace(/&#10;/g, '\n')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
    text.split(/[\n,]/).forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed.includes('|')) return;
      const [label, range] = trimmed.split('|').map((part) => part.trim());
      if (!label || !range) return;
      const dash = range.indexOf('-');
      if (dash < 0) return;
      const min = range.slice(0, dash) === '' ? null : Number(range.slice(0, dash));
      const max = range.slice(dash + 1) === '' ? null : Number(range.slice(dash + 1));
      const out = {};
      if (min != null && !Number.isNaN(min) && min > 0) out.gte = min;
      if (max != null && !Number.isNaN(max)) out.lte = max;
      map[label] = out;
    });
    return map;
  }
  function filterButton(id) {
    return [...document.querySelectorAll('[data-filter-id]')].find((el) => el.dataset.filterId === id);
  }
  function normToken(value) {
    return String(value || '').toLowerCase().replace(/&amp;/g, '&').replace(/[^a-z0-9]+/g, '');
  }
  function matchTokens(btn, fallback) {
    return String(btn?.dataset.filterMatch || fallback || '')
      .split('|')
      .map(normToken)
      .filter(Boolean);
  }
  function typeValueForCategory(category) {
    const normalized = String(category || '').toUpperCase();
    const btn = qsa('[data-aa-shop-tabs] [data-tab]', shop)
      .find((el) => (el.dataset.tab || '').toUpperCase() === normalized);
    return btn?.dataset.tabValue || category;
  }
  const parsedBuckets = parsePriceBuckets(shop.dataset.aaPriceBuckets);
  function priceUnit() {
    const code = String(shop.dataset.aaCurrency || '').toUpperCase();
    if (code === 'JPY' || code === 'KRW' || code === 'VND' || code === 'CLP') return 1;
    return 100;
  }
  const PRICE_MAP = Object.keys(parsedBuckets).length
    ? parsedBuckets
    : {
      'Under $10': { lte: 10 },
      '$10 – $20': { gte: 10, lte: 20 },
      '$20 – $30': { gte: 20, lte: 30 },
      '$30+': { gte: 30 },
    };

  const state = {
    category: firstTab,
    sort: SORT_LABEL[defaultSort] || 'FEATURED',
    sortValue: defaultSort,
    filters: [],
    page: 1,
    quantity: 1,
    selectedOptions: {},
    product: null,
  };

  const drawer = document.querySelector('[data-aa-filter-drawer]');
  const shade = document.querySelector('[data-aa-filter-shade]');
  const modal = document.querySelector('[data-aa-quick-view-modal]');
  const empty = shop.querySelector('[data-aa-shop-empty]');
  const countEl = shop.querySelector('[data-aa-product-count]');
  const paginationRoot = shop.querySelector('[data-aa-fallback-pagination]');
  const cards = () => qsa('[data-aa-product]', shop);

  function lockScroll(on) {
    document.body.style.overflow = on ? 'hidden' : '';
  }

  function setFiltersOpen(open) {
    if (drawer) drawer.hidden = !open;
    if (shade) shade.hidden = !open;
    lockScroll(open || (modal && !modal.hidden));
  }

  function setModalOpen(open) {
    if (!modal) return;
    modal.hidden = !open;
    lockScroll(open || (drawer && !drawer.hidden));
  }

  function syncFilterButtons() {
    document.querySelectorAll('[data-filter-id]').forEach((btn) => {
      const id = btn.dataset.filterId || '';
      let on = state.filters.includes(id);
      if (navMode === 'tabs' && id.startsWith('CATEGORY:')) {
        const label = id.slice('CATEGORY:'.length).toUpperCase();
        const value = String(btn.dataset.filterValue || '').toUpperCase();
        const current = String(state.category || '').toUpperCase();
        on = label === current || (value !== '' && value === current);
      }
      btn.classList.toggle('active', on);
      const mark = btn.querySelector('i');
      if (mark) mark.innerHTML = on
        ? '<svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M20 6 9 17l-5-5"/></svg>'
        : '';
    });
    const filterBtn = shop.querySelector('[data-aa-open-filters]');
    if (filterBtn) {
      filterBtn.classList.toggle('active', state.filters.length > 0);
      const countLabel = filterBtn.querySelector('[data-aa-filter-count]');
      if (countLabel) countLabel.textContent = state.filters.length ? ` (${state.filters.length})` : '';
    }
    const result = document.querySelector('[data-aa-filter-result-count]');
    if (result) result.textContent = String(visibleCount());
  }

  function visibleCount() {
    if (!live) return matchedCards().length;
    const label = countEl?.textContent || '';
    const n = Number((label.match(/\d+/) || ['0'])[0]);
    return n;
  }

  function updateCount(n) {
    if (countEl) countEl.textContent = `${n} ${countWord}`;
    const result = document.querySelector('[data-aa-filter-result-count]');
    if (result) result.textContent = String(n);
  }

  function readUrlState() {
    const p = params();
    const alias = p.get('sort');
    if (alias && SORT_ALIAS[alias] && !p.get('sort_by')) {
      if (live) {
        p.set('sort_by', SORT_ALIAS[alias]);
        p.delete('sort');
        p.delete('page');
        location.replace(`${location.pathname}?${p}`);
        return true;
      }
      state.sortValue = SORT_ALIAS[alias];
      state.sort = SORT_LABEL[state.sortValue] || 'FEATURED';
    } else {
      state.sortValue = p.get('sort_by') || defaultSort;
      state.sort = SORT_LABEL[state.sortValue] || 'FEATURED';
    }
    state.category = firstTab;
    if (navMode === 'tabs') {
      state.category = (p.get('category') || firstTab).toUpperCase();
      const typeFilters = p.getAll('filter.p.product_type');
      if (typeFilters.length === 1) {
        const match = qsa('[data-aa-shop-tabs] [data-tab]', shop).find((el) => {
          const tabValue = el.dataset.tabValue || el.dataset.tab || '';
          return tabValue.toUpperCase() === typeFilters[0].toUpperCase()
            || (el.dataset.tab || '').toUpperCase() === typeFilters[0].toUpperCase();
        });
        state.category = (match?.dataset.tab || typeFilters[0]).toUpperCase();
      }
    }
    state.filters = [];
    const artistButtons = () => [...document.querySelectorAll('[data-filter-group="ARTIST"] [data-filter-id]')];
    const artistParams = new Set([artistParam, 'filter.p.m.custom.artist', 'filter.p.m.custom.artists']);
    artistButtons().forEach((btn) => {
      if (btn.dataset.filterParam) artistParams.add(btn.dataset.filterParam);
    });
    artistParams.forEach((param) => {
      p.getAll(param).forEach((value) => {
        const btn = artistButtons().find((el) => el.dataset.filterValue === value || el.dataset.filterId === `ARTIST:${value}`);
        const id = btn?.dataset.filterId || `ARTIST:${value}`;
        if (!state.filters.includes(id)) state.filters.push(id);
      });
    });
    if (shop.dataset.aaArtistSource !== 'metafield') {
      p.getAll('filter.p.vendor').forEach((value) => {
        const id = `ARTIST:${value}`;
        if (!state.filters.includes(id)) state.filters.push(id);
      });
    }
    document.querySelectorAll('[data-filter-id][data-filter-param]').forEach((btn) => {
      const param = btn.dataset.filterParam;
      const value = btn.dataset.filterValue;
      const id = btn.dataset.filterId;
      if (!param || param === 'filter.v.price' || !id) return;
      if (value && p.getAll(param).includes(value) && !state.filters.includes(id)) state.filters.push(id);
    });
    p.getAll('filter.p.m.custom.badges').forEach((value) => {
      const btn = [...document.querySelectorAll('[data-filter-value]')]
        .find((el) => (el.dataset.filterValue || '').toLowerCase() === value.toLowerCase());
      const id = btn?.dataset.filterId;
      if (id && !state.filters.includes(id)) state.filters.push(id);
    });
    p.getAll('filter.p.m.custom.fulfillment').forEach((value) => {
      const token = normToken(value);
      let id = '';
      if (token.includes('preorder')) id = 'FULFILLMENT:Preorder';
      else if (token.includes('madetoorder')) id = 'FULFILLMENT:Made to Order';
      else if (token.includes('instock')) id = 'FULFILLMENT:In Stock';
      if (id && !state.filters.includes(id)) state.filters.push(id);
    });
    p.getAll('filter.p.tag').forEach((value) => {
      const tagId = `TAGS:${value}`;
      if (state.filters.includes(tagId)) return;
      const badgeBtn = [...document.querySelectorAll('[data-filter-group="BADGE"] [data-filter-id]')]
        .find((el) => el.dataset.filterValue === value || (el.dataset.filterId || '').endsWith(`:${value}`));
      if (badgeBtn?.dataset.filterId && !state.filters.includes(badgeBtn.dataset.filterId)) {
        state.filters.push(badgeBtn.dataset.filterId);
        return;
      }
      state.filters.push(tagId);
    });
    const unit = priceUnit();
    const gteRaw = p.get('filter.v.price.gte');
    const lteRaw = p.get('filter.v.price.lte');
    const gte = gteRaw == null || gteRaw === '' ? null : Number(gteRaw) / unit;
    const lte = lteRaw == null || lteRaw === '' ? null : Number(lteRaw) / unit;
    if (gte != null || lte != null) {
      const match = Object.entries(PRICE_MAP).find(([, range]) => {
        const gteMatch = range.gte == null ? gte == null : gte === range.gte;
        const lteMatch = range.lte == null ? lte == null : lte === range.lte;
        return gteMatch && lteMatch;
      });
      if (match && !state.filters.includes(`PRICE:${match[0]}`)) state.filters.push(`PRICE:${match[0]}`);
    }
    state.filters = navMode === 'tabs'
      ? [...new Set(state.filters.filter((id) => !id.startsWith('CATEGORY:')))]
      : [...new Set(state.filters)];
    state.page = Number(p.get('page') || 1);
    return false;
  }

  function catalogPath() {
    return String(collectionUrl || location.pathname).split('?')[0];
  }

  function liveUrl({ category = state.category, sortValue = state.sortValue, filters = state.filters } = {}) {
    const current = params();
    const p = new URLSearchParams();
    ['q', 'type'].forEach((key) => {
      const value = current.get(key);
      if (value) p.set(key, value);
    });
    current.forEach((value, key) => {
      if (key.startsWith('options[') && value) p.append(key, value);
    });
    if (sortValue) p.set('sort_by', sortValue);
    const activeCategory = String(category || '').toUpperCase();
    const tabIsAll = !activeCategory || activeCategory === String(firstTab || 'ALL').toUpperCase() || activeCategory === 'ALL';
    if (navMode === 'tabs' && !tabIsAll) {
      p.append('filter.p.product_type', typeValueForCategory(activeCategory));
    }
    filters.forEach((id) => {
      const idx = id.indexOf(':');
      const group = id.slice(0, idx);
      const value = id.slice(idx + 1);
      const btn = filterButton(id);
      if (group === 'CATEGORY') {
        if (navMode === 'tabs') return;
        if (btn?.dataset.filterParam && btn.dataset.filterValue) p.append(btn.dataset.filterParam, btn.dataset.filterValue);
        return;
      }
      if (group === 'PRICE') {
        const range = PRICE_MAP[value];
        const unit = priceUnit();
        if (range?.gte != null) p.set('filter.v.price.gte', String(Math.round(range.gte * unit)));
        if (range?.lte != null) p.set('filter.v.price.lte', String(Math.round(range.lte * unit)));
        return;
      }
      if (btn?.dataset.filterParam && btn.dataset.filterValue) {
        if (btn.dataset.filterParam === 'filter.v.availability') p.set(btn.dataset.filterParam, btn.dataset.filterValue);
        else p.append(btn.dataset.filterParam, btn.dataset.filterValue);
        return;
      }
      if (group === 'ARTIST') {
        p.append(btn?.dataset.filterParam || artistParam, btn?.dataset.filterValue || value);
      }
      if (group === 'STATUS' || group === 'BADGE') {
        if (/^in stock$/i.test(value)) p.set('filter.v.availability', '1');
        else p.append('filter.p.m.custom.badges', btn?.dataset.filterValue || value);
      }
      if (group === 'TAGS') p.append('filter.p.tag', value);
    });
    const query = p.toString();
    const path = catalogPath();
    return query ? `${path}?${query}` : path;
  }

  function goLive(next) {
    location.assign(liveUrl(next));
  }

  function syncClientUrl() {
    if (live) return;
    const next = liveUrl();
    const current = `${location.pathname}${location.search}`;
    if (next !== current) history.pushState({ aaShop: true }, '', next);
  }

  function cardMatches(card) {
    const requiredSeries = String(shop.dataset.aaSeriesHandle || '').toLowerCase();
    if (requiredSeries && card.dataset.seriesFeed === 'true') {
      const handles = String(card.dataset.seriesHandles || '').toLowerCase().split('|').filter(Boolean);
      if (!handles.includes(requiredSeries)) return false;
    }
    const category = (card.dataset.category || '').toUpperCase();
    const current = String(state.category || '').toUpperCase();
    const allTab = String(firstTab || 'ALL').toUpperCase();
    if (current && current !== allTab && current !== 'ALL' && category !== current) return false;
    const artistTokens = [card.dataset.artist, card.dataset.artistHandle].map(normToken).filter(Boolean);
    const price = Number(card.dataset.price || 0);
    const tags = (card.dataset.tags || '').toLowerCase();
    const tagToken = normToken(card.dataset.tags);
    const badge = (card.dataset.badge || '').toLowerCase();
    const badgeToken = normToken(card.dataset.badge);
    const available = card.dataset.available !== 'false';
    const groups = {};
    state.filters.forEach((id) => {
      const idx = id.indexOf(':');
      const group = id.slice(0, idx);
      const value = id.slice(idx + 1);
      (groups[group] ||= []).push(value);
    });
    if (groups.ARTIST) {
      const ok = groups.ARTIST.some((value) => {
        const tokens = matchTokens(filterButton(`ARTIST:${value}`), value);
        return tokens.some((token) => artistTokens.includes(token));
      });
      if (!ok) return false;
    }
    if (groups.CATEGORY) {
      const ok = groups.CATEGORY.some((value) => {
        const tokens = matchTokens(filterButton(`CATEGORY:${value}`), value);
        const cardToken = normToken(category);
        return category === value.toUpperCase() || tokens.includes(cardToken);
      });
      if (!ok) return false;
    }
    const badgeValues = [...(groups.STATUS || []), ...(groups.BADGE || [])];
    if (badgeValues.length) {
      const ok = badgeValues.every((value) => {
        const tokens = matchTokens(filterButton(`BADGE:${value}`) || filterButton(`STATUS:${value}`), value);
        const lower = value.toLowerCase();
        if (tokens.includes('instock') || lower === 'in stock') return available;
        if (tokens.some((token) => token === 'new' || token === 'newarrival')) {
          return badge === 'new' || badge === 'new arrival' || badgeToken === 'new' || /\bnew\b/.test(tags) || tagToken.includes('newarrival');
        }
        return tokens.some((token) => {
          if (!token || token === 'instock') return false;
          return badgeToken === token || badgeToken.includes(token) || tagToken.includes(token) || badge.includes(lower);
        });
      });
      if (!ok) return false;
    }
    if (groups.FULFILLMENT) {
      const current = normToken(card.dataset.fulfillment || 'instock');
      const ok = groups.FULFILLMENT.some((value) => {
        const tokens = matchTokens(filterButton(`FULFILLMENT:${value}`), value);
        return tokens.includes(current) || normToken(value) === current;
      });
      if (!ok) return false;
    }
    if (groups.AVAILABILITY) {
      const ok = groups.AVAILABILITY.some((value) => {
        const token = normToken(value);
        if (token.includes('out') || token === '0') return !available;
        return available;
      });
      if (!ok) return false;
    }
    if (groups.SERIES) {
      const handles = String(card.dataset.seriesHandles || '').toLowerCase().split('|').filter(Boolean).map(normToken);
      const ok = groups.SERIES.some((value) => {
        const btn = filterButton(`SERIES:${value}`);
        const tokens = [value, btn?.dataset.filterValue, btn?.textContent].map(normToken).filter(Boolean);
        return handles.some((handle) => tokens.includes(handle));
      });
      if (!ok) return false;
    }
    if (groups.PRICE) {
      const ok = groups.PRICE.every((bucket) => {
        const range = PRICE_MAP[bucket];
        if (!range) return true;
        if (range.gte != null && price < range.gte) return false;
        if (range.lte != null && price > range.lte) return false;
        return true;
      });
      if (!ok) return false;
    }
    if (groups.TAGS && !groups.TAGS.every((tag) => tags.includes(tag.toLowerCase()))) return false;
    return true;
  }

  function cardCents(card) {
    if (card.hasAttribute('data-price-cents')) {
      const cents = Number(card.dataset.priceCents);
      if (Number.isFinite(cents)) return cents;
    }
    const raw = String(card.dataset.price || '').replace(/[^0-9.]/g, '');
    const amount = Number(raw);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  function cardStamp(card) {
    const created = Number(card.dataset.created || 0);
    return Number.isFinite(created) ? created : 0;
  }

  function cardOrder(card) {
    const index = Number(card.dataset.index || 0);
    return Number.isFinite(index) ? index : 0;
  }

  function sortCards(list, sortValue) {
    const value = sortValue || 'manual';
    const sorted = [...list];
    if (value === 'created-descending') {
      sorted.sort((a, b) => cardStamp(b) - cardStamp(a) || cardOrder(a) - cardOrder(b));
    } else if (value === 'price-ascending') {
      sorted.sort((a, b) => cardCents(a) - cardCents(b) || cardOrder(a) - cardOrder(b));
    } else if (value === 'price-descending') {
      sorted.sort((a, b) => cardCents(b) - cardCents(a) || cardOrder(a) - cardOrder(b));
    } else {
      sorted.sort((a, b) => cardOrder(a) - cardOrder(b));
    }
    return sorted;
  }

  function matchedCards() {
    return sortCards(cards().filter(cardMatches), state.sortValue);
  }

  function pageItems(page, totalPages) {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const items = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) items.push('…');
    for (let value = start; value <= end; value += 1) items.push(value);
    if (end < totalPages - 1) items.push('…');
    items.push(totalPages);
    return items;
  }

  function renderFallbackPagination(page, totalPages) {
    if (!paginationRoot) return;
    if (totalPages <= 1) {
      paginationRoot.innerHTML = '';
      return;
    }
    const prevDisabled = page === 1 ? 'disabled' : '';
    const nextDisabled = page === totalPages ? 'disabled' : '';
    paginationRoot.innerHTML = `<nav class="aa-pagination" aria-label="Product pages">${
      `<button type="button" data-page="${page - 1}" ${prevDisabled} aria-label="Previous page"><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>`
    }${pageItems(page, totalPages)
      .map((item) =>
        item === '…'
          ? '<span aria-hidden>…</span>'
          : `<button type="button" data-page-number="${item}" class="${item === page ? 'active' : ''}" ${item === page ? 'aria-current="page"' : ''}>${item}</button>`,
      )
      .join('')}${
      `<button type="button" data-page="${page + 1}" ${nextDisabled} aria-label="Next page"><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>`
    }</nav>`;
  }

  function renderFallback() {
    const matched = matchedCards();
    const grid = shop.querySelector('.product-grid-system, .shop-product-grid');
    if (grid) {
      const rest = cards().filter((card) => !matched.includes(card));
      [...matched, ...rest].forEach((card) => grid.appendChild(card));
    }
    const totalPages = Math.max(1, Math.ceil(matched.length / pageSize));
    if (state.page > totalPages) state.page = totalPages;
    const start = (state.page - 1) * pageSize;
    const shown = new Set(matched.slice(start, start + pageSize));
    cards().forEach((card) => {
      const hide = !shown.has(card);
      card.hidden = hide;
      card.classList.toggle('is-filtered-out', hide);
    });
    updateCount(matched.length);
    if (empty) {
      empty.hidden = matched.length > 0;
      const browsing = state.filters.length === 0 && (!currentCategory() || currentCategory() === 'ALL');
      const heading = empty.querySelector('h2');
      const copy = empty.querySelector('p');
      if (heading && empty.dataset.emptyHeading) {
        heading.textContent = browsing
          ? empty.dataset.emptyHeading
          : (empty.dataset.emptyFilteredHeading || empty.dataset.emptyHeading);
      }
      if (copy && empty.dataset.emptyText) {
        copy.textContent = browsing
          ? empty.dataset.emptyText
          : (empty.dataset.emptyFilteredText || empty.dataset.emptyText);
      }
      const action = empty.querySelector('[data-aa-empty-action]');
      if (action) {
        const filtered = state.filters.length > 0;
        const onlyTab = !filtered && currentCategory() !== 'ALL';
        action.hidden = !filtered && !onlyTab;
        action.toggleAttribute('data-aa-reset-catalog', onlyTab);
        if (filtered && action.dataset.labelFilters) action.textContent = action.dataset.labelFilters;
        if (onlyTab && action.dataset.labelAll) action.textContent = action.dataset.labelAll;
      }
    }
    renderFallbackPagination(state.page, matched.length === 0 ? 0 : totalPages);
    syncFilterButtons();
    syncTabsAndSort();
    renderActiveFilters();
  }

  function currentCategory() {
    const current = String(state.category || '').toUpperCase();
    const allTab = String(firstTab || 'ALL').toUpperCase();
    if (!current || current === allTab) return 'ALL';
    return current;
  }

  function syncTabsAndSort() {
    const current = currentCategory();
    qsa('[data-aa-shop-tabs] [data-tab]', shop).forEach((btn) => {
      const tab = String(btn.dataset.tab || '').toUpperCase();
      const value = String(btn.dataset.tabValue || '').toUpperCase();
      btn.classList.toggle('active', tab === current || (value !== '' && value === current));
    });
    const label = shop.querySelector('[data-aa-sort-label]');
    if (label) label.textContent = state.sort;
    qsa('[data-aa-sort-menu] button', shop).forEach((btn) => {
      const on = btn.dataset.sortValue === state.sortValue || btn.dataset.sortLabel === state.sort;
      btn.classList.toggle('active', on);
      btn.setAttribute('aria-selected', String(on));
      const icon = btn.querySelector('svg');
      if (icon && !on) icon.remove();
    });
    const activeSort = shop.querySelector('[data-aa-sort-menu] button.active');
    if (activeSort && !activeSort.querySelector('svg')) {
      activeSort.insertAdjacentHTML(
        'beforeend',
        '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
      );
    }
    const activeTab = shop.querySelector('[data-aa-shop-tabs] button.active');
    const tabNav = activeTab?.closest('[data-aa-shop-tabs]');
    if (activeTab && tabNav) {
      const left = activeTab.offsetLeft - (tabNav.clientWidth - activeTab.clientWidth) / 2;
      tabNav.scrollTo({ left: Math.max(0, left) });
    }
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderActiveFilters() {
    const root = shop.querySelector('[data-aa-active-filters]');
    if (!root || live) return;
    const items = [];
    if (navMode === 'tabs' && currentCategory() !== 'ALL') {
      items.push({ id: `TAB:${state.category}`, label: currentCategory() });
    }
    state.filters.forEach((id) => {
      items.push({ id, label: id.slice(id.indexOf(':') + 1) });
    });
    if (!items.length) {
      root.hidden = true;
      root.innerHTML = '';
      return;
    }
    root.hidden = false;
    root.innerHTML = `${items.map((item) =>
      `<button type="button" data-aa-chip-remove="${escapeHtml(item.id)}">${escapeHtml(item.label)} <span aria-hidden="true">×</span></button>`,
    ).join('')}<button type="button" data-aa-clear-all>Clear all</button>`;
  }

  function realOptions(product) {
    return (product.options || []).filter((option) => {
      const values = option.values || [];
      if (!values.length) return false;
      if (option.name === 'Title' && (values.length === 1 || values[0] === 'Default Title')) return false;
      if (values.length === 1 && values[0] === 'Default Title') return false;
      return true;
    });
  }

  function findVariant(product) {
    const variants = product.variants || [];
    const options = realOptions(product);
    if (!options.length) return variants[0] || { id: product.variantId, price: product.price, available: product.available };
    return (
      variants.find((variant) =>
        (variant.options || []).every((value, index) => {
          const name = product.options?.[index]?.name;
          return !name || state.selectedOptions[name] === value;
        }),
      ) || variants[0]
    );
  }

  function fillQuickView(product) {
    state.product = product;
    state.quantity = 1;
    const options = realOptions(product);
    state.selectedOptions = Object.fromEntries(options.map((option) => [option.name, option.values[0] || '']));
    modal.querySelector('[data-qv-image]').src = product.image || '';
    modal.querySelector('[data-qv-image]').alt = product.title || '';
    modal.querySelector('[data-qv-type]').textContent = product.type || '';
    modal.querySelector('[data-qv-title]').textContent = product.title || '';
    const artist = modal.querySelector('[data-qv-artist]');
    const artistName = typeof product.artist === 'string' ? product.artist.trim() : '';
    if (artist) {
      artist.hidden = artistName === '';
      if (artistName) {
        artist.textContent = `BY ${artistName.toUpperCase()} →`;
        artist.href = product.artistUrl || product.url || '#';
      }
    }
    modal.querySelector('[data-qv-details]').href = product.url || '#';
    modal.querySelector('[data-aa-quick-view-dialog]').setAttribute('aria-label', product.title || 'Quick view');
    const optionRoot = modal.querySelector('[data-qv-options]');
    optionRoot.innerHTML = options
      .map(
        (option) => `<fieldset class="shop-modal-option"><legend>${option.name}</legend><div>${option.values
          .map(
            (value) =>
              `<button type="button" data-option-name="${option.name}" data-option-value="${value}" class="${state.selectedOptions[option.name] === value ? 'active' : ''}">${value}</button>`,
          )
          .join('')}</div></fieldset>`,
      )
      .join('');
    updateQuickViewVariant();
  }

  function updateQuickViewVariant() {
    if (!state.product) return;
    const variant = findVariant(state.product);
    const price = variant?.price || state.product.price;
    const compare = variant ? variant.compare : state.product.compare;
    window.aaPaintPrice?.(modal.querySelector('[data-qv-price]'), price, compare);
    modal.querySelector('[data-qv-qty]').textContent = String(state.quantity);
    const available = variant?.available !== false && state.product.available !== false;
    window.aaPaintFulfillment?.(modal, variant?.fulfillment || state.product.fulfillment, available);
    const add = modal.querySelector('[data-qv-add]');
    if (add) {
      add.disabled = !available;
      if (variant?.id) add.dataset.variantId = String(variant.id);
      else add.removeAttribute('data-variant-id');
    }
    state.currentVariant = variant;
    window.aaSyncQuickViewMedia?.(variant, state.product);
  }

  async function loadCatalogFeed() {
    const meta = shop.querySelector('[data-aa-feed-meta]');
    let feed = shop.dataset.aaFeedUrl || meta?.dataset.url || '';
    let pages = Number(shop.dataset.aaFeedPages || meta?.dataset.pages || 0);
    let start = Number(shop.dataset.aaFeedStart || meta?.dataset.start || 2);
    if (shop.dataset.aaFeedKeepQuery === 'true' && feed) {
      const base = feed.split('?')[0];
      const kept = new URLSearchParams(location.search);
      kept.delete('page');
      const query = kept.toString();
      feed = query ? `${base}?${query}` : base;
    }
    if (!feed || !pages || start > pages) return;
    const grid = shop.querySelector('.product-grid-system, .shop-product-grid');
    if (!grid) return;
    const seriesHandle = String(shop.dataset.aaSeriesHandle || '').toLowerCase();
    const seen = new Set(
      [...grid.querySelectorAll('[data-handle]')].map((card) => String(card.dataset.handle || '').toLowerCase()).filter(Boolean),
    );
    for (let page = start; page <= pages; page += 1) {
      const join = feed.includes('?') ? '&' : '?';
      let response;
      try {
        response = await fetch(`${feed}${join}page=${page}`, { credentials: 'same-origin' });
      } catch (err) {
        break;
      }
      if (!response || !response.ok) break;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const feedRoot = doc.querySelector('[data-aa-product-feed]');
      const next = [...(feedRoot || doc).querySelectorAll('[data-aa-product]')];
      if (!next.length) break;
      next.forEach((node) => {
        const handle = String(node.dataset.handle || '').toLowerCase();
        if (handle && seen.has(handle)) return;
        if (seriesHandle) {
          const handles = String(node.dataset.seriesHandles || '').toLowerCase().split('|').filter(Boolean);
          if (!handles.includes(seriesHandle)) return;
        }
        node.dataset.seriesFeed = 'true';
        if (handle) seen.add(handle);
        grid.appendChild(document.importNode(node, true));
      });
    }
  }

  function ensureCategoryTabs() {
    const nav = shop.querySelector('[data-aa-shop-tabs]');
    if (!nav || !shop.dataset.aaSeriesHandle) return;
    const seen = new Set(
      [...nav.querySelectorAll('[data-tab]')].map((btn) => String(btn.dataset.tab || '').toUpperCase()),
    );
    cards().forEach((card) => {
      const cat = String(card.dataset.category || '').toUpperCase();
      if (!cat || seen.has(cat)) return;
      seen.add(cat);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.tab = cat;
      btn.dataset.tabValue = cat;
      btn.textContent = cat;
      nav.appendChild(btn);
    });
  }

  function clampLivePage() {
    if (!live) return false;
    const meta = shop.querySelector('[data-aa-page-meta]');
    const current = Number(meta?.dataset.current || params().get('page') || 1);
    const pages = Number(meta?.dataset.pages || 0);
    if (current > 1 && (cards().length === 0 || (pages > 0 && current > pages))) {
      const next = params();
      next.delete('page');
      const query = next.toString();
      location.replace(query ? `${location.pathname}?${query}` : location.pathname);
      return true;
    }
    return false;
  }

  try {
    if (readUrlState()) return;
    await loadCatalogFeed();
    ensureCategoryTabs();
    if (live) {
      if (clampLivePage()) return;
      syncFilterButtons();
      syncTabsAndSort();
    } else {
      renderFallback();
    }
  } catch (err) {
    console.warn('Shop catalog init failed.', err);
  }

  shop.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-aa-shop-tabs] [data-tab]');
    if (tab) {
      state.category = tab.dataset.tab || firstTab;
      state.filters = state.filters.filter((id) => !id.startsWith('CATEGORY:'));
      state.page = 1;
      if (live) {
        goLive();
        return;
      }
      syncClientUrl();
      renderFallback();
      return;
    }

    const sortToggle = event.target.closest('[data-aa-sort-toggle]');
    if (sortToggle) {
      const wrap = sortToggle.closest('[data-aa-sort]');
      const menu = wrap?.querySelector('[data-aa-sort-menu]');
      const open = menu?.hidden;
      qsa('[data-aa-sort-menu]', shop).forEach((el) => {
        el.hidden = true;
      });
      qsa('[data-aa-sort]', shop).forEach((el) => el.classList.remove('open'));
      if (menu) {
        menu.hidden = !open;
        wrap.classList.toggle('open', Boolean(open));
        sortToggle.setAttribute('aria-expanded', String(Boolean(open)));
      }
      return;
    }

    const sortOption = event.target.closest('[data-aa-sort-menu] button');
    if (sortOption) {
      const wrap = sortOption.closest('[data-aa-sort]');
      wrap?.querySelector('[data-aa-sort-menu]')?.setAttribute('hidden', '');
      wrap?.classList.remove('open');
      const nextSort = sortOption.dataset.sortValue || 'manual';
      state.sort = sortOption.dataset.sortLabel || state.sort;
      state.sortValue = nextSort;
      state.page = 1;
      if (live) {
        goLive();
        return;
      }
      const nextParams = params();
      nextParams.delete('sort');
      nextParams.delete('page');
      nextParams.set('sort_by', nextSort);
      const nextQuery = nextParams.toString();
      history.replaceState(null, '', nextQuery ? `${location.pathname}?${nextQuery}` : location.pathname);
      renderFallback();
      return;
    }

    const pageBtn = event.target.closest('[data-aa-fallback-pagination] [data-page], [data-aa-fallback-pagination] [data-page-number]');
    if (pageBtn && !pageBtn.disabled) {
      const next = Number(pageBtn.dataset.pageNumber || pageBtn.dataset.page);
      if (next >= 1) {
        state.page = next;
        renderFallback();
        shop.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });

  document.addEventListener('pointerdown', (event) => {
    if (event.target.closest('[data-aa-sort]')) return;
    qsa('[data-aa-sort]', shop).forEach((el) => {
      el.classList.remove('open');
      const menu = el.querySelector('[data-aa-sort-menu]');
      if (menu) menu.hidden = true;
    });
  });

  document.addEventListener('click', (event) => {
    const chipLink = event.target.closest('[data-aa-filter-chip]');
    if (chipLink?.href) {
      event.preventDefault();
      const url = new URL(chipLink.href, location.origin);
      url.searchParams.delete('page');
      location.assign(`${url.pathname}${url.search}${url.hash}`);
      return;
    }
    const clearAll = event.target.closest('[data-aa-clear-all]');
    if (clearAll && clearAll.tagName !== 'A') {
      event.preventDefault();
      state.category = firstTab;
      state.filters = [];
      state.page = 1;
      if (live) goLive();
      else {
        syncClientUrl();
        renderFallback();
      }
      return;
    }
    const chipRemove = event.target.closest('[data-aa-chip-remove]');
    if (chipRemove) {
      const id = chipRemove.dataset.aaChipRemove || '';
      if (id.startsWith('TAB:')) state.category = firstTab;
      else state.filters = state.filters.filter((item) => item !== id);
      state.page = 1;
      if (live) goLive();
      else {
        syncClientUrl();
        renderFallback();
      }
      return;
    }
    if (event.target.closest('[data-aa-open-filters]')) {
      setFiltersOpen(true);
      return;
    }
    if (event.target.closest('[data-aa-close-filters]') || event.target.closest('[data-aa-filter-shade]')) {
      setFiltersOpen(false);
      return;
    }
    if (event.target.closest('[data-aa-clear-filters]')) {
      const clearBtn = event.target.closest('[data-aa-clear-filters]');
      if (clearBtn && clearBtn.hasAttribute('data-aa-reset-catalog')) state.category = firstTab;
      state.filters = [];
      state.page = 1;
      if (live) {
        goLive();
        return;
      }
      setFiltersOpen(false);
      syncClientUrl();
      renderFallback();
      return;
    }
    if (event.target.closest('[data-aa-apply-filters]')) {
      state.page = 1;
      if (live) {
        goLive();
        return;
      }
      setFiltersOpen(false);
      renderFallback();
      return;
    }
    const filterBtn = event.target.closest('[data-filter-id]');
    if (filterBtn) {
      event.preventDefault();
      const id = filterBtn.dataset.filterId;
      if (navMode === 'tabs' && id.startsWith('CATEGORY:')) {
        const label = id.slice('CATEGORY:'.length).toUpperCase();
        const valueUp = String(filterBtn.dataset.filterValue || '').toUpperCase();
        const current = String(state.category || '').toUpperCase();
        const turningOn = current !== label && current !== valueUp;
        state.filters = state.filters.filter((item) => !item.startsWith('CATEGORY:'));
        state.category = turningOn ? (label || valueUp || firstTab) : firstTab;
        state.page = 1;
        if (live) goLive();
        else {
          syncClientUrl();
          renderFallback();
        }
        return;
      }
      const turningOn = !state.filters.includes(id);
      state.filters = turningOn
        ? [...state.filters, id]
        : state.filters.filter((item) => item !== id);
      if (turningOn && (id.startsWith('PRICE:') || id.startsWith('AVAILABILITY:'))) {
        const prefix = id.slice(0, id.indexOf(':') + 1);
        state.filters = state.filters.filter((item) => item === id || !item.startsWith(prefix));
      }
      state.page = 1;
      if (live) goLive();
      else {
        syncClientUrl();
        renderFallback();
      }
      return;
    }
    const viewAll = event.target.closest('[data-aa-view-all], .shop-empty a');
    if (viewAll) {
      event.preventDefault();
      const href = viewAll.getAttribute('href');
      if (live && href) {
        location.assign(href);
        return;
      }
      if (live) {
        location.assign(catalogPath());
        return;
      }
      state.category = firstTab;
      state.filters = [];
      state.page = 1;
      syncClientUrl();
      renderFallback();
      return;
    }
  });

  window.addEventListener('popstate', () => {
    if (live) return;
    readUrlState();
    renderFallback();
  });

  const artistSearch = document.querySelector('[data-aa-artist-search]');
  const artistClear = document.querySelector('[data-aa-artist-search-clear]');
  artistSearch?.addEventListener('input', () => {
    const q = artistSearch.value.trim().toLowerCase();
    if (artistClear) artistClear.hidden = !q;
    document.querySelectorAll('[data-filter-group="ARTIST"] [data-filter-id]').forEach((btn) => {
      const label = (btn.textContent || '').toLowerCase();
      btn.hidden = Boolean(q) && !label.includes(q);
    });
  });
  artistClear?.addEventListener('click', () => {
    artistSearch.value = '';
    artistClear.hidden = true;
    document.querySelectorAll('[data-filter-group="ARTIST"] [data-filter-id]').forEach((btn) => {
      btn.hidden = false;
    });
  });
})();

(() => {
  const root = document.querySelector('[data-aa-pdp]');
  if (!root) return;

  const summary = root.querySelector('[data-aa-summary]');
  const gallery = root.querySelector('[data-aa-gallery]');
  const jsonEl = summary?.querySelector('[data-aa-pdp-json]');
  let product = null;
  try {
    product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
  } catch (err) {
    console.warn('Product data missing.', err);
  }

  const state = {
    quantity: 1,
    selectedOptions: {},
    currentVariant: product?.variants?.[0] || null,
  };

  function realOptions(data) {
    return (data?.options || []).filter((option) => {
      const values = option.values || [];
      if (!values.length) return false;
      if (option.name === 'Title' && (values.length === 1 || values[0] === 'Default Title')) return false;
      if (values.length === 1 && values[0] === 'Default Title') return false;
      return true;
    });
  }

  if (product) {
    const options = realOptions(product);
    const first = product.variants?.[0];
    state.selectedOptions = Object.fromEntries(
      options.map((option, index) => [option.name, first?.options?.[index] || option.values[0] || '']),
    );
    state.currentVariant =
      product.variants.find((variant) =>
        (variant.options || []).every((value, index) => {
          const name = product.options?.[index]?.name;
          return !name || state.selectedOptions[name] === value;
        }),
      ) || first;
  }

  function findVariant() {
    if (!product) return null;
    const options = realOptions(product);
    const variants = product.variants || [];
    if (!options.length) return variants[0] || state.currentVariant;
    return (
      variants.find((variant) =>
        (variant.options || []).every((value, index) => {
          const name = product.options?.[index]?.name;
          return !name || state.selectedOptions[name] === value;
        }),
      ) || null
    );
  }

  function showPanel(panel, scrollInto) {
    if (!gallery || !panel) return;
    const mobile = window.matchMedia('(max-width: 760px)').matches;
    gallery.querySelectorAll('[data-aa-gallery-panel]').forEach((item) => {
      const on = item === panel;
      item.classList.toggle('is-active', on);
      if (!on) {
        item.querySelectorAll('video').forEach((video) => {
          video.pause();
        });
      }
    });
    gallery.querySelectorAll('[data-aa-gallery-thumb]').forEach((btn) => {
      const on = btn.dataset.mediaId === panel.dataset.mediaId;
      btn.classList.toggle('active', on);
      if (on) btn.setAttribute('aria-current', 'true');
      else btn.removeAttribute('aria-current');
    });
    const zoom = gallery.querySelector('[data-aa-gallery-zoom]');
    if (zoom) zoom.hidden = panel.dataset.type !== 'image';
    const image = panel.querySelector('[data-aa-gallery-main]');
    const lightbox = document.querySelector('[data-aa-lightbox-image]');
    if (lightbox && image) lightbox.src = image.currentSrc || image.src;
    if (scrollInto && mobile) {
      const stage = gallery.querySelector('[data-aa-gallery-stage]');
      if (stage) stage.scrollTo({ left: panel.offsetLeft, behavior: 'smooth' });
    }
  }

  function setGalleryImage(src) {
    if (!src || !gallery) return;
    const panels = Array.from(gallery.querySelectorAll('[data-aa-gallery-panel]'));
    const matched = panels.find((panel) => panel.dataset.type === 'image' && panel.dataset.src === src);
    if (matched) {
      showPanel(matched, true);
      return;
    }
    const imagePanel =
      panels.find((panel) => panel.dataset.type === 'image' && panel.classList.contains('is-active')) ||
      panels.find((panel) => panel.dataset.type === 'image');
    const main = imagePanel?.querySelector('[data-aa-gallery-main]');
    const backdrop = imagePanel?.querySelector('[data-aa-gallery-backdrop]');
    if (main) main.src = src;
    if (backdrop) backdrop.src = src;
    if (imagePanel) {
      imagePanel.dataset.src = src;
      showPanel(imagePanel, true);
    }
    const lightbox = document.querySelector('[data-aa-lightbox-image]');
    if (lightbox) lightbox.src = src;
  }

  function setLightbox(open) {
    const box = document.querySelector('[data-aa-lightbox]');
    if (!box) return;
    if (open && box.parentElement !== document.body) {
      document.body.appendChild(box);
    }
    box.hidden = !open;
    document.body.style.overflow = open ? 'hidden' : '';
  }

  const stickyBar = document.querySelector('[data-aa-sticky-atc]');
  const stickyAdd = stickyBar?.querySelector('[data-aa-sticky-add]');
  const stickyThumb = stickyBar?.querySelector('[data-aa-sticky-thumb]');
  const stickyPrice = stickyBar?.querySelector('[data-aa-sticky-price]');
  const stickyLabel = stickyBar?.querySelector('[data-aa-sticky-label]');
  const stickyIcon = stickyBar?.querySelector('[data-aa-sticky-icon]');
  let adding = false;

  function variantThumb(variant) {
    return variant?.thumb || variant?.image || product?.featured_thumb || product?.featured_image || '';
  }

  function syncSticky() {
    if (!stickyBar) return;
    const variant = state.currentVariant;
    const available = Boolean(variant) && variant.available !== false;
    const info = variant?.fulfillment || {};
    const preorder = available && info.mode === 'preorder';
    const soldLabel = stickyAdd?.dataset.soldoutLabel || 'Sold Out';
    let addLabel = stickyAdd?.dataset.addLabel || 'Add to Cart';
    if (!variant) addLabel = 'Select an Option';
    else if (!available) addLabel = soldLabel;
    else if (preorder) addLabel = 'Pre-order';
    if (stickyPrice && variant?.price) window.aaPaintPrice?.(stickyPrice, variant.price, variant.compare);
    if (stickyThumb) {
      const src = variantThumb(variant);
      stickyThumb.hidden = !src;
      if (src) stickyThumb.src = src;
    }
    if (stickyAdd) {
      stickyAdd.dataset.variantId = variant?.id || '';
      stickyAdd.disabled = adding || !available;
      if (stickyLabel) stickyLabel.textContent = addLabel;
      if (stickyIcon) stickyIcon.hidden = !available;
    }
  }

  function setStickyVisible(show) {
    if (!stickyBar) return;
    stickyBar.classList.toggle('is-visible', show);
    stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (show) stickyBar.removeAttribute('inert');
    else stickyBar.setAttribute('inert', '');
  }

  function bindStickyBar() {
    if (!stickyBar) return;
    if (stickyBar.parentElement !== document.body) {
      document.querySelectorAll('body > [data-aa-sticky-atc]').forEach((el) => {
        if (el !== stickyBar) el.remove();
      });
      document.body.appendChild(stickyBar);
    }
    const mainAdd = summary?.querySelector('[data-aa-pdp-add]');
    const footer = document.querySelector('.footer-theme-shell') || document.querySelector('footer');
    let atcPassed = false;
    let footerInView = false;
    const update = () => setStickyVisible(atcPassed && !footerInView);
    if (typeof IntersectionObserver === 'function') {
      if (mainAdd) {
        const atcObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          atcPassed = Boolean(entry) && !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          update();
        });
        atcObserver.observe(mainAdd);
      }
      if (footer) {
        const footerObserver = new IntersectionObserver(
          (entries) => {
            footerInView = entries.some((entry) => entry.isIntersecting);
            update();
          },
          { rootMargin: '0px 0px -24px 0px' },
        );
        footerObserver.observe(footer);
      }
      update();
    }
    stickyAdd?.addEventListener('click', (event) => {
      event.preventDefault();
      addCurrentToCart(stickyAdd);
    });
  }

  function addCurrentToCart(trigger) {
    const variant = state.currentVariant;
    if (adding || trigger?.disabled || variant?.available === false) return;
    const variantId = trigger?.dataset.variantId || variant?.id;
    if (!variantId) return;
    const mainAdd = summary?.querySelector('[data-aa-pdp-add]');
    adding = true;
    if (stickyAdd) stickyAdd.disabled = true;
    if (mainAdd && trigger !== mainAdd) mainAdd.disabled = true;
    Promise.resolve(
      window.aaAddVariantToCart?.({
        variantId,
        quantity: state.quantity,
        live: true,
        properties: window.aaLineProperties?.(variant),
      }),
    )
      .then((added) => {
        if (added) return window.aaRevealCart?.();
        window.aaOpenCart?.();
      })
      .catch((err) => {
        console.warn('Add to cart failed.', err);
        window.aaOpenCart?.();
      })
      .finally(() => {
        adding = false;
        syncVariant();
      });
  }

  function syncVariant() {
    const variant = findVariant();
    state.currentVariant = variant;
    const add = summary?.querySelector('[data-aa-pdp-add]');
    const price = summary?.querySelector('[data-aa-pdp-price]');
    const qty = summary?.querySelector('[data-aa-pdp-qty]');
    const available = Boolean(variant) && variant.available !== false;
    const info = variant?.fulfillment || {};
    const preorder = available && info.mode === 'preorder';
    const made = available && info.mode === 'made_to_order';
    if (price && variant?.price) window.aaPaintPrice?.(price, variant.price, variant.compare);
    const stateBadge = summary?.querySelector('[data-aa-badge-state]');
    const badgeRow = summary?.querySelector('[data-aa-badges]');
    if (stateBadge) {
      if (!variant) stateBadge.textContent = '';
      else if (!available) stateBadge.textContent = 'Sold Out';
      else if (preorder) stateBadge.textContent = 'Pre-order';
      else stateBadge.textContent = '';
      stateBadge.hidden = !stateBadge.textContent;
    }
    if (badgeRow) {
      const visible = badgeRow.querySelector('.detail-badge:not([hidden])');
      badgeRow.hidden = !visible;
    }
    const copy = summary?.querySelector('[data-aa-preorder-copy]');
    if (copy) copy.hidden = !preorder;
    const intro = summary?.querySelector('[data-aa-preorder-intro]');
    const detail = summary?.querySelector('[data-aa-preorder-detail]');
    if (intro) {
      intro.hidden = !info.intro;
      intro.textContent = info.intro || '';
    }
    if (detail) {
      detail.hidden = !info.detail;
      detail.textContent = info.detail || '';
    }
    const hint = summary?.querySelector('[data-aa-ship-hint]');
    if (hint) {
      if ((preorder || made) && info.detail) hint.textContent = info.detail;
      else if (preorder) hint.textContent = hint.dataset.hintPreorder || '';
      else hint.textContent = hint.dataset.hintStandard || '';
      hint.hidden = !hint.textContent.trim();
    }
    if (qty) qty.textContent = String(state.quantity);
    if (add) {
      add.dataset.variantId = variant?.id || '';
      if (!variant) {
        add.disabled = true;
        if (!adding) add.textContent = add.dataset.selectLabel || 'Select an Option';
      } else if (!available) {
        add.disabled = true;
        if (!adding) add.textContent = add.dataset.soldoutLabel || 'Sold Out';
      } else if (preorder) {
        add.disabled = adding;
        if (!adding) add.textContent = add.dataset.preorderLabel || 'Pre-order';
      } else {
        add.disabled = adding;
        if (!adding) add.textContent = add.dataset.addLabel || 'Add to Cart';
      }
    }
    if (variant?.image) setGalleryImage(variant.image);
    syncSticky();
  }

  root.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-aa-gallery-thumb]');
    if (thumb && gallery) {
      const panel = gallery.querySelector(`[data-aa-gallery-panel][data-media-id="${thumb.dataset.mediaId}"]`);
      if (panel) showPanel(panel, true);
      else if (thumb.dataset.src) setGalleryImage(thumb.dataset.src);
      return;
    }
    const zoom = event.target.closest('[data-aa-gallery-zoom]');
    const mainImage = event.target.closest('[data-aa-gallery-main]');
    if (zoom || mainImage) {
      event.preventDefault();
      const panel = mainImage?.closest('[data-aa-gallery-panel]')
        || gallery?.querySelector('[data-aa-gallery-panel].is-active');
      const image = panel?.querySelector('[data-aa-gallery-main]');
      if (!image) return;
      const lightbox = document.querySelector('[data-aa-lightbox-image]');
      if (lightbox) lightbox.src = image.currentSrc || image.src;
      setLightbox(true);
      return;
    }
    const optionBtn = event.target.closest('[data-aa-pdp-options] [data-option-name]');
    if (optionBtn) {
      state.selectedOptions[optionBtn.dataset.optionName] = optionBtn.dataset.optionValue;
      summary.querySelectorAll(`[data-option-name="${optionBtn.dataset.optionName}"]`).forEach((btn) => {
        btn.classList.toggle('active', btn === optionBtn);
      });
      syncVariant();
      return;
    }
    if (event.target.closest('[data-aa-pdp-minus]')) {
      const max = state.currentVariant?.inventory > 0 ? state.currentVariant.inventory : Infinity;
      state.quantity = Math.max(1, state.quantity - 1);
      if (state.quantity > max) state.quantity = max;
      syncVariant();
      return;
    }
    if (event.target.closest('[data-aa-pdp-plus]')) {
      const max = state.currentVariant?.inventory > 0 ? state.currentVariant.inventory : Infinity;
      state.quantity = Math.min(max === Infinity ? state.quantity + 1 : max, state.quantity + 1);
      syncVariant();
      return;
    }
    const add = event.target.closest('[data-aa-pdp-add]');
    if (add) {
      event.preventDefault();
      if (adding || add.disabled) return;
      const variantId = add.dataset.variantId || state.currentVariant?.id;
      const label = add.dataset.addLabel || 'ADD TO CART';
      const added = add.dataset.addedLabel || 'ADDED TO CART';
      adding = true;
      add.disabled = true;
      if (stickyAdd) stickyAdd.disabled = true;
      Promise.resolve(
        window.aaAddVariantToCart?.({
          variantId,
          quantity: state.quantity,
          live: true,
          properties: window.aaLineProperties?.(state.currentVariant),
        }),
      )
        .then((result) => {
          add.textContent = added;
          if (result) return window.aaRevealCart?.();
          window.aaOpenCart?.();
        })
        .then(() => {
          window.setTimeout(() => {
            adding = false;
            if (state.currentVariant?.available !== false) add.textContent = label;
            syncVariant();
          }, 1600);
        })
        .catch((err) => {
          console.warn('Add to cart failed.', err);
          window.aaOpenCart?.();
          adding = false;
          syncVariant();
        });
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-aa-lightbox-close]') || event.target.matches('[data-aa-lightbox]')) {
      setLightbox(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setLightbox(false);
  });

  const galleryStage = gallery?.querySelector('[data-aa-gallery-stage]');
  galleryStage?.addEventListener('scroll', () => {
    if (!window.matchMedia('(max-width: 760px)').matches) return;
    const panels = Array.from(gallery.querySelectorAll('[data-aa-gallery-panel]'));
    const left = galleryStage.scrollLeft;
    let closest = null;
    let best = Infinity;
    panels.forEach((panel) => {
      const delta = Math.abs(panel.offsetLeft - left);
      if (delta < best) {
        best = delta;
        closest = panel;
      }
    });
    if (closest && !closest.classList.contains('is-active')) showPanel(closest, false);
  }, { passive: true });

  bindAsideFollow();
  bindStickyBar();
  syncSticky();

  function bindAsideFollow() {
    const aside = summary?.classList.contains('detail-aside')
      ? summary
      : document.querySelector('.detail-page .detail-aside');
    if (!aside) return;
    const gap = 20;
    const desktop = window.matchMedia('(min-width: 761px)');
    let lastY = window.scrollY;
    let top = gap;

    function place(dy) {
      if (!desktop.matches) {
        aside.style.top = '';
        top = gap;
        return;
      }
      const height = aside.offsetHeight;
      const maxTop = gap;
      const minTop = Math.min(gap, window.innerHeight - height - gap);
      if (minTop >= maxTop) {
        top = maxTop;
        aside.style.top = `${top}px`;
        return;
      }
      const rectTop = aside.getBoundingClientRect().top;
      if (rectTop > maxTop + 1) top = maxTop;
      else top = Math.min(maxTop, Math.max(minTop, top - dy));
      aside.style.top = `${top}px`;
    }

    place(0);
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      const dy = y - lastY;
      lastY = y;
      place(dy);
    }, { passive: true });
    window.addEventListener('resize', () => {
      lastY = window.scrollY;
      place(0);
    });
    aside.addEventListener('toggle', () => {
      lastY = window.scrollY;
      place(0);
    }, true);
  }
})();

(() => {
  const recs = document.querySelector('[data-aa-recs]');
  if (!recs?.dataset.url) return;
  fetch(recs.dataset.url, { credentials: 'same-origin' })
    .then((response) => (response.ok ? response.text() : ''))
    .then((html) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-aa-recs]');
      if (!next) return;
      const hasCards = next.querySelector('[data-aa-product]');
      if (!hasCards) {
        if (!recs.querySelector('[data-aa-product]')) recs.hidden = true;
        return;
      }
      next.hidden = false;
      recs.replaceWith(next);
    })
    .catch(() => {
      if (!recs.querySelector('[data-aa-product]')) recs.hidden = true;
    });
})();

(() => {
  const catalogs = [...document.querySelectorAll('[data-aa-artist-catalog], [data-aa-series-catalog]')];
  if (!catalogs.length) return;

  function cards(root) {
    return [...root.querySelectorAll('[data-aa-product]')];
  }

  function categoryKey(value) {
    const text = String(value || '').toUpperCase().trim();
    const raw = text.replace(/[^A-Z0-9]+/g, '');
    if (!raw) return '';
    if (raw === 'ALL') return 'ALL';
    if (raw.includes('KEYCHAIN') || text.includes('CHARM')) return 'KEYCHAINS';
    if (raw.includes('POSTCARD') || raw.includes('STICKER') || text.includes('PRINT')) return 'PRINTS';
    if (raw.includes('MOUSE') || text.includes('DESK')) return 'DESK';
    if (/(SHIRT|HOOD|SARONG|APPAREL|TOTE)/.test(raw) || text.includes('HAT')) return 'APPAREL';
    if (/(BADGE|MAGNET|CLOCK|COLLECTIBLE|FIGURE)/.test(raw) || text.includes('PIN')) return 'COLLECTIBLES';
    if (raw.includes('FRESHENER') || text.includes('ACCESSOR')) return 'ACCESSORIES';
    if (/(HOME|LIFESTYLE|MUG|PILLOW|RUG|TOWEL)/.test(raw)) return 'HOME';
    return text;
  }

  function apply(root, state) {
    const list = cards(root);
    const firstTab = (root.dataset.aaFirstTab || 'ALL').toUpperCase();
    const category = categoryKey(state.category || firstTab);
    const visible = list.filter((card) => category === firstTab || categoryKey(card.dataset.category) === category);
    const cents = (card) => {
      if (card.hasAttribute('data-price-cents')) {
        const fromCents = Number(card.dataset.priceCents);
        if (Number.isFinite(fromCents)) return fromCents;
      }
      const amount = Number(String(card.dataset.price || '').replace(/[^0-9.]/g, ''));
      return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
    };
    const stamp = (card) => Number(card.dataset.created || 0) || 0;
    const order = (card) => Number(card.dataset.index || 0) || 0;
    const ordered = [...visible].sort((a, b) => {
      if (state.sortValue === 'created-descending') return stamp(b) - stamp(a) || order(a) - order(b);
      if (state.sortValue === 'price-ascending') return cents(a) - cents(b) || order(a) - order(b);
      if (state.sortValue === 'price-descending') return cents(b) - cents(a) || order(a) - order(b);
      return order(a) - order(b);
    });
    const grid = root.querySelector('.product-grid-system');
    if (grid) {
      ordered.forEach((card) => grid.appendChild(card));
      list.filter((card) => !visible.includes(card)).forEach((card) => grid.appendChild(card));
    }
    list.forEach((card) => {
      card.hidden = !visible.includes(card);
    });
    const count = root.querySelector('[data-aa-product-count]');
    if (count) count.textContent = `${visible.length} PRODUCTS`;
    root.querySelectorAll('[data-aa-shop-tabs] [data-tab]').forEach((btn) => {
      btn.classList.toggle('active', categoryKey(btn.dataset.tab) === category);
    });
    const label = root.querySelector('[data-aa-sort-label]');
    if (label) label.textContent = state.sort;
    root.querySelectorAll('[data-aa-sort-menu] button').forEach((btn) => {
      const on = btn.dataset.sortValue === state.sortValue || (!btn.dataset.sortValue && btn.dataset.sortLabel === state.sort);
      btn.classList.toggle('active', on);
      btn.setAttribute('aria-selected', String(on));
      const icon = btn.querySelector('svg');
      if (icon && !on) icon.remove();
    });
    const active = root.querySelector('[data-aa-sort-menu] button.active');
    if (active && !active.querySelector('svg')) {
      active.insertAdjacentHTML(
        'beforeend',
        '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
      );
    }
  }

  const SORT_LABELS = {
    manual: 'FEATURED',
    'best-selling': 'FEATURED',
    'created-descending': 'NEWEST',
    'created-ascending': 'NEWEST',
    'price-ascending': 'PRICE: LOW TO HIGH',
    'price-descending': 'PRICE: HIGH TO LOW',
  };

  function sortFromLocation() {
    const value = new URLSearchParams(location.search).get('sort_by') || 'manual';
    return { sortValue: value, sort: SORT_LABELS[value] || 'FEATURED' };
  }

  function writeSortUrl(sortValue) {
    const params = new URLSearchParams(location.search);
    params.delete('sort');
    params.delete('page');
    if (sortValue) params.set('sort_by', sortValue);
    const query = params.toString();
    history.replaceState(null, '', query ? `${location.pathname}?${query}` : location.pathname);
  }

  catalogs.forEach((root) => {
    const initialSort = sortFromLocation();
    const state = {
      category: root.dataset.aaFirstTab || 'ALL',
      sort: initialSort.sort,
      sortValue: initialSort.sortValue,
    };
    apply(root, state);
    root.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-aa-shop-tabs] [data-tab]');
      if (tab) {
        state.category = tab.dataset.tab || 'ALL';
        apply(root, state);
        return;
      }
      const sortToggle = event.target.closest('[data-aa-sort-toggle]');
      if (sortToggle && root.contains(sortToggle)) {
        const wrap = sortToggle.closest('[data-aa-sort]');
        const menu = wrap?.querySelector('[data-aa-sort-menu]');
        const open = Boolean(menu?.hidden);
        root.querySelectorAll('[data-aa-sort-menu]').forEach((el) => {
          el.hidden = true;
        });
        root.querySelectorAll('[data-aa-sort]').forEach((el) => el.classList.remove('open'));
        if (menu) {
          menu.hidden = !open;
          wrap.classList.toggle('open', open);
          sortToggle.setAttribute('aria-expanded', String(open));
        }
        return;
      }
      const sortOption = event.target.closest('[data-aa-sort-menu] button');
      if (sortOption && root.contains(sortOption)) {
        const wrap = sortOption.closest('[data-aa-sort]');
        wrap?.querySelector('[data-aa-sort-menu]')?.setAttribute('hidden', '');
        wrap?.classList.remove('open');
        state.sort = sortOption.dataset.sortLabel || SORT_LABELS[sortOption.dataset.sortValue] || 'FEATURED';
        state.sortValue = sortOption.dataset.sortValue || 'manual';
        writeSortUrl(state.sortValue);
        apply(root, state);
      }
    });
  });

  document.addEventListener('pointerdown', (event) => {
    if (event.target.closest('[data-aa-artist-catalog] [data-aa-sort], [data-aa-series-catalog] [data-aa-sort]')) return;
    catalogs.forEach((root) => {
      root.querySelectorAll('[data-aa-sort]').forEach((el) => {
        el.classList.remove('open');
        const menu = el.querySelector('[data-aa-sort-menu]');
        if (menu) menu.hidden = true;
        const toggle = el.querySelector('[data-aa-sort-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    });
  });
})();

(() => {
  const root = document.querySelector('[data-aa-collections-directory]');
  if (!root) return;

  const scope = root.parentElement || document;
  const pageSize = Number(root.dataset.aaPageSize || 8);
  const countLabel = root.dataset.aaCountLabel || 'COLLECTIONS';
  const tabs = [...root.querySelectorAll('[data-aa-directory-tabs] [data-category]')];
  const firstTab = tabs[0];
  const search = root.querySelector('[data-aa-directory-search]');
  const searchClear = root.querySelector('[data-aa-directory-search-clear]');
  const countValue = root.querySelector('[data-aa-directory-count-value]');
  const filterButton = root.querySelector('[data-aa-collection-open-filters]');
  const filterLabel = root.querySelector('[data-aa-collection-filter-label]');
  const empty = root.querySelector('[data-aa-collections-empty]');
  const paginationRoot = root.querySelector('[data-aa-collections-pagination]');
  const shade = scope.querySelector('[data-aa-collection-filter-shade]');
  const drawer = scope.querySelector('[data-aa-collection-filter-drawer]');
  const resultCount = scope.querySelector('[data-aa-collection-result-count]');
  const state = {
    query: '',
    category: firstTab?.dataset.category || 'ALL',
    match: (firstTab?.dataset.match || 'ALL').toUpperCase(),
    filters: [],
    page: 1,
  };

  function cards() {
    return [...root.querySelectorAll('[data-aa-collection-card]')];
  }

  function pageItems(page, totalPages) {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const items = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) items.push('…');
    for (let value = start; value <= end; value += 1) items.push(value);
    if (end < totalPages - 1) items.push('…');
    items.push(totalPages);
    return items;
  }

  function matchesCount(count, filters) {
    const selected = filters.filter((id) => id.startsWith('PRODUCT COUNT:'));
    if (!selected.length) return true;
    return selected.some((id) => {
      const value = id.slice('PRODUCT COUNT:'.length);
      if (value === 'Under 15') return count < 15;
      if (value === '15–19' || value === '15-19') return count >= 15 && count <= 19;
      if (value === '20+') return count >= 20;
      return false;
    });
  }

  function cardMatches(card) {
    const type = (card.dataset.type || '').toUpperCase();
    const artist = card.dataset.artist || '';
    const count = Number(card.dataset.count || 0);
    const query = state.query.trim().toLowerCase();
    const haystack = (card.dataset.search || card.dataset.title || '').toLowerCase();
    if (query && !haystack.includes(query)) return false;
    if (state.category.toUpperCase() !== 'ALL') {
      const match = state.match.toUpperCase();
      const typeOk = type === match || (match === 'STORY WORLDS' && type === 'STORY');
      if (!typeOk) return false;
    }
    const artistFilters = state.filters.filter((id) => id.startsWith('ARTIST:'));
    if (artistFilters.length && !artistFilters.includes(`ARTIST:${artist}`)) return false;
    const typeFilters = state.filters.filter((id) => id.startsWith('COLLECTION TYPE:'));
    if (typeFilters.length && !typeFilters.some((id) => id.slice('COLLECTION TYPE:'.length).toUpperCase() === type)) return false;
    if (!matchesCount(count, state.filters)) return false;
    return true;
  }

  function setDrawer(open) {
    if (shade) shade.hidden = !open;
    if (drawer) drawer.hidden = !open;
    document.documentElement.toggleAttribute('scroll-lock', open);
  }

  function syncChrome(matchedCount) {
    if (countValue) countValue.textContent = String(matchedCount);
    if (resultCount) resultCount.textContent = String(matchedCount);
    if (filterButton) filterButton.classList.toggle('active', state.filters.length > 0);
    if (filterLabel) filterLabel.textContent = state.filters.length ? `FILTER (${state.filters.length})` : 'FILTER';
    tabs.forEach((tab) => {
      tab.classList.toggle('active', tab.dataset.category === state.category);
    });
    scope.querySelectorAll('[data-aa-collection-filter]').forEach((button) => {
      const on = state.filters.includes(button.dataset.aaCollectionFilter);
      button.classList.toggle('active', on);
    });
    if (empty) empty.hidden = matchedCount > 0;
  }

  function renderPagination(page, totalPages, matchedCount) {
    if (!paginationRoot) return;
    if (totalPages <= 1) {
      paginationRoot.innerHTML = '';
      return;
    }
    const start = matchedCount === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, matchedCount);
    const status = matchedCount ? `Showing ${start}–${end} of ${matchedCount} Collections` : '';
    const prevDisabled = page === 1 ? 'disabled' : '';
    const nextDisabled = page === totalPages ? 'disabled' : '';
    const chevronLeft = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>';
    const chevronRight = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';
    paginationRoot.innerHTML = `<nav class="aa-pagination" aria-label="Collection pages">${
      `<button type="button" data-page="${page - 1}" ${prevDisabled} aria-label="Previous page">${chevronLeft}</button>`
    }${pageItems(page, totalPages)
      .map((item) =>
        item === '…'
          ? '<span aria-hidden>…</span>'
          : `<button type="button" data-page-number="${item}" class="${item === page ? 'active' : ''}" ${item === page ? 'aria-current="page"' : ''}>${item}</button>`,
      )
      .join('')}${
      `<button type="button" data-page="${page + 1}" ${nextDisabled} aria-label="Next page">${chevronRight}</button>`
    }${status ? `<p class="aa-pagination-status">${status}</p>` : ''}</nav>`;
  }

  function render(scroll) {
    const matched = cards().filter(cardMatches);
    const totalPages = Math.max(1, Math.ceil(matched.length / pageSize));
    if (state.page > totalPages) state.page = totalPages;
    if (state.page < 1) state.page = 1;
    const start = (state.page - 1) * pageSize;
    const shown = new Set(matched.slice(start, start + pageSize));
    cards().forEach((card) => {
      card.hidden = !shown.has(card);
    });
    syncChrome(matched.length);
    renderPagination(state.page, matched.length === 0 ? 0 : totalPages, matched.length);
    if (scroll) {
      root.querySelector('.collections-directory')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function resetAll() {
    state.query = '';
    state.filters = [];
    state.page = 1;
    state.category = firstTab?.dataset.category || 'ALL';
    state.match = (firstTab?.dataset.match || 'ALL').toUpperCase();
    if (search) search.value = '';
    if (searchClear) searchClear.hidden = true;
    render(false);
  }

  root.addEventListener('input', (event) => {
    if (event.target !== search) return;
    state.query = search.value;
    if (searchClear) searchClear.hidden = !state.query;
    state.page = 1;
    render(false);
  });

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-aa-directory-search-clear]')) {
      state.query = '';
      if (search) search.value = '';
      if (searchClear) searchClear.hidden = true;
      state.page = 1;
      render(false);
      return;
    }
    const tab = event.target.closest('[data-aa-directory-tabs] [data-category]');
    if (tab) {
      state.category = tab.dataset.category || 'ALL';
      state.match = (tab.dataset.match || state.category).toUpperCase();
      state.page = 1;
      render(false);
      return;
    }
    if (event.target.closest('[data-aa-collections-reset]')) {
      resetAll();
      return;
    }
    const pageBtn = event.target.closest('[data-aa-collections-pagination] [data-page], [data-aa-collections-pagination] [data-page-number]');
    if (pageBtn && !pageBtn.disabled) {
      const next = Number(pageBtn.dataset.pageNumber || pageBtn.dataset.page);
      if (next >= 1) {
        state.page = next;
        render(true);
      }
    }
  });

  scope.addEventListener('click', (event) => {
    if (event.target.closest('[data-aa-collection-open-filters]')) {
      setDrawer(true);
      return;
    }
    if (event.target.closest('[data-aa-collection-close-filters]') || event.target.closest('[data-aa-collection-filter-shade]')) {
      setDrawer(false);
      return;
    }
    if (event.target.closest('[data-aa-collection-apply-filters]')) {
      setDrawer(false);
      return;
    }
    if (event.target.closest('[data-aa-collection-clear-filters]')) {
      state.filters = [];
      state.page = 1;
      render(false);
      return;
    }
    const filterBtn = event.target.closest('[data-aa-collection-filter]');
    if (filterBtn) {
      const id = filterBtn.dataset.aaCollectionFilter;
      state.filters = state.filters.includes(id)
        ? state.filters.filter((item) => item !== id)
        : [...state.filters, id];
      state.page = 1;
      render(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer && !drawer.hidden) setDrawer(false);
  });

  render(false);
})();

(() => {
  const root = document.querySelector('[data-aa-artists-directory]');
  if (!root) return;

  const scope = root.parentElement || document;
  const pageSize = Number(root.dataset.aaPageSize || 12);
  const statusLabel = root.dataset.aaStatusLabel || 'Artists';
  const search = root.querySelector('[data-aa-directory-search]');
  const searchClear = root.querySelector('[data-aa-directory-search-clear]');
  const countValue = root.querySelector('[data-aa-directory-count-value]');
  const headingCount = root.querySelector('[data-aa-artists-heading-count]');
  const filterButton = root.querySelector('[data-aa-collection-open-filters]');
  const filterLabel = root.querySelector('[data-aa-collection-filter-label]');
  const empty = root.querySelector('[data-aa-artists-empty]');
  const paginationRoot = root.querySelector('[data-aa-artists-pagination]');
  const grid = root.querySelector('[data-aa-artists-grid]');
  const shade = scope.querySelector('[data-aa-collection-filter-shade]');
  const drawer = scope.querySelector('[data-aa-collection-filter-drawer]');
  const resultCount = scope.querySelector('[data-aa-collection-result-count]');
  const sortRoot = root.querySelector('[data-aa-directory-sort]');
  const sortMenu = root.querySelector('[data-aa-directory-sort-menu]');
  const sortToggle = root.querySelector('[data-aa-directory-sort-toggle]');
  const sortLabel = root.querySelector('[data-aa-directory-sort-label]');
  const checkTemplate = sortMenu?.querySelector('svg')?.cloneNode(true);
  const state = {
    query: '',
    filters: [],
    sort: sortLabel?.textContent?.trim() || 'FEATURED',
    page: 1,
  };

  function cards() {
    return [...root.querySelectorAll('[data-aa-artist-card]')];
  }

  function pageItems(page, totalPages) {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const items = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) items.push('…');
    for (let value = start; value <= end; value += 1) items.push(value);
    if (end < totalPages - 1) items.push('…');
    items.push(totalPages);
    return items;
  }

  function cardMatches(card) {
    const query = state.query.trim().toLowerCase();
    const haystack = (card.dataset.search || card.dataset.name || '').toLowerCase();
    if (query && !haystack.includes(query)) return false;
    return true;
  }

  function sorted(list) {
    const mode = state.sort.toUpperCase();
    return [...list].sort((a, b) => {
      if (mode.startsWith('NAME')) return (a.dataset.name || '').localeCompare(b.dataset.name || '');
      if (mode.startsWith('PRODUCTS')) {
        return Number(b.dataset.count || 0) - Number(a.dataset.count || 0) || Number(a.dataset.order || 0) - Number(b.dataset.order || 0);
      }
      return Number(b.dataset.featured || 0) - Number(a.dataset.featured || 0) || Number(a.dataset.order || 0) - Number(b.dataset.order || 0);
    });
  }

  function setDrawer(open) {
    if (shade) shade.hidden = !open;
    if (drawer) drawer.hidden = !open;
    document.documentElement.toggleAttribute('scroll-lock', open);
  }

  function closeSort() {
    if (!sortRoot || !sortMenu || !sortToggle) return;
    sortMenu.hidden = true;
    sortRoot.classList.remove('open');
    sortToggle.setAttribute('aria-expanded', 'false');
  }

  function setSortUI(label) {
    if (sortLabel) sortLabel.textContent = label;
    sortMenu?.querySelectorAll('button').forEach((button) => {
      const on = button.dataset.sortLabel === label;
      button.classList.toggle('active', on);
      button.setAttribute('aria-selected', on ? 'true' : 'false');
      button.querySelector('svg')?.remove();
      if (on && checkTemplate) button.appendChild(checkTemplate.cloneNode(true));
    });
  }

  function syncChrome(matchedCount) {
    if (countValue) countValue.textContent = String(matchedCount);
    if (headingCount) headingCount.textContent = String(matchedCount);
    if (resultCount) resultCount.textContent = String(matchedCount);
    if (filterButton) filterButton.classList.toggle('active', state.filters.length > 0);
    if (filterLabel) filterLabel.textContent = state.filters.length ? `FILTER (${state.filters.length})` : 'FILTER';
    scope.querySelectorAll('[data-aa-collection-filter]').forEach((button) => {
      const on = state.filters.includes(button.dataset.aaCollectionFilter);
      button.classList.toggle('active', on);
    });
    if (empty) empty.hidden = matchedCount > 0;
    setSortUI(state.sort);
  }

  function renderPagination(page, totalPages, matchedCount) {
    if (!paginationRoot) return;
    if (totalPages <= 1) {
      paginationRoot.innerHTML = '';
      return;
    }
    const start = matchedCount === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, matchedCount);
    const status = matchedCount ? `Showing ${start}–${end} of ${matchedCount} ${statusLabel}` : '';
    const prevDisabled = page === 1 ? 'disabled' : '';
    const nextDisabled = page === totalPages ? 'disabled' : '';
    const chevronLeft = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>';
    const chevronRight = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';
    paginationRoot.innerHTML = `<nav class="aa-pagination" aria-label="Artist pages">${
      `<button type="button" data-page="${page - 1}" ${prevDisabled} aria-label="Previous page">${chevronLeft}</button>`
    }${pageItems(page, totalPages)
      .map((item) =>
        item === '…'
          ? '<span aria-hidden>…</span>'
          : `<button type="button" data-page-number="${item}" class="${item === page ? 'active' : ''}" ${item === page ? 'aria-current="page"' : ''}>${item}</button>`,
      )
      .join('')}${
      `<button type="button" data-page="${page + 1}" ${nextDisabled} aria-label="Next page">${chevronRight}</button>`
    }${status ? `<p class="aa-pagination-status">${status}</p>` : ''}</nav>`;
  }

  function render() {
    const ordered = sorted(cards());
    if (grid) ordered.forEach((card) => grid.appendChild(card));
    const matched = ordered.filter(cardMatches);
    const totalPages = Math.max(1, Math.ceil(matched.length / pageSize));
    if (state.page > totalPages) state.page = totalPages;
    if (state.page < 1) state.page = 1;
    const start = (state.page - 1) * pageSize;
    const shown = new Set(matched.slice(start, start + pageSize));
    ordered.forEach((card) => {
      card.hidden = !shown.has(card);
    });
    syncChrome(matched.length);
    renderPagination(state.page, matched.length === 0 ? 0 : totalPages, matched.length);
  }

  function resetAll() {
    state.query = '';
    state.filters = [];
    state.page = 1;
    if (search) search.value = '';
    if (searchClear) searchClear.hidden = true;
    render();
  }

  root.addEventListener('input', (event) => {
    if (event.target !== search) return;
    state.query = search.value;
    if (searchClear) searchClear.hidden = !state.query;
    state.page = 1;
    render();
  });

  scope.addEventListener('click', (event) => {
    const sortToggleHit = event.target.closest('[data-aa-directory-sort-toggle]');
    if (sortToggleHit && root.contains(sortToggleHit) && sortMenu && sortRoot) {
      const open = sortMenu.hidden;
      sortMenu.hidden = !open;
      sortRoot.classList.toggle('open', open);
      sortToggleHit.setAttribute('aria-expanded', String(open));
      return;
    }
    const sortOption = event.target.closest('[data-aa-directory-sort-menu] button');
    if (sortOption && root.contains(sortOption)) {
      state.sort = sortOption.dataset.sortLabel || 'FEATURED';
      state.page = 1;
      closeSort();
      render();
      return;
    }
    if (event.target.closest('[data-aa-directory-search-clear]')) {
      state.query = '';
      if (search) search.value = '';
      if (searchClear) searchClear.hidden = true;
      state.page = 1;
      render();
      return;
    }
    if (event.target.closest('[data-aa-artists-reset]')) {
      resetAll();
      return;
    }
    const pageBtn = event.target.closest('[data-aa-artists-pagination] [data-page], [data-aa-artists-pagination] [data-page-number]');
    if (pageBtn && !pageBtn.disabled) {
      const next = Number(pageBtn.dataset.pageNumber || pageBtn.dataset.page);
      if (next >= 1) {
        state.page = next;
        render();
      }
      return;
    }
    if (event.target.closest('[data-aa-collection-open-filters]')) {
      setDrawer(true);
      return;
    }
    if (event.target.closest('[data-aa-collection-close-filters]') || event.target.closest('[data-aa-collection-filter-shade]')) {
      setDrawer(false);
      return;
    }
    if (event.target.closest('[data-aa-collection-apply-filters]')) {
      setDrawer(false);
      return;
    }
    if (event.target.closest('[data-aa-collection-clear-filters]')) {
      state.filters = [];
      state.page = 1;
      render();
      return;
    }
    const filterBtn = event.target.closest('[data-aa-collection-filter]');
    if (filterBtn && scope.contains(filterBtn)) {
      const id = filterBtn.dataset.aaCollectionFilter;
      state.filters = state.filters.includes(id) ? state.filters.filter((item) => item !== id) : [...state.filters, id];
      state.page = 1;
      render();
    }
  });

  document.addEventListener('pointerdown', (event) => {
    if (!sortRoot || sortRoot.contains(event.target)) return;
    closeSort();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeSort();
    if (drawer && !drawer.hidden) setDrawer(false);
  });

  render();
})();

(() => {
  const root = document.querySelector('[data-aa-home-series-counts]');
  if (!root) return;

  async function syncHomeSeriesProductCounts() {
    const feed = root.dataset.aaProductFeedUrl || '';
    const pages = Number(root.dataset.aaProductFeedPages || 0);
    if (!feed || !pages) return;
    const counts = new Map();
    for (let page = 1; page <= pages; page += 1) {
      const join = feed.includes('?') ? '&' : '?';
      let response;
      try {
        response = await fetch(`${feed}${join}page=${page}`, { credentials: 'same-origin' });
      } catch (err) {
        break;
      }
      if (!response?.ok) break;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const products = [...doc.querySelectorAll('[data-aa-product]')];
      if (!products.length) break;
      products.forEach((product) => {
        const handles = new Set(
          String(product.dataset.seriesHandles || '')
            .toLowerCase()
            .split('|')
            .map((handle) => handle.trim())
            .filter(Boolean),
        );
        handles.forEach((handle) => counts.set(handle, (counts.get(handle) || 0) + 1));
      });
    }
    root.querySelectorAll('[data-aa-series-card][data-series-handle]').forEach((card) => {
      const handle = String(card.dataset.seriesHandle || '').toLowerCase();
      const count = counts.get(handle) || 0;
      card.dataset.count = String(count);
      const label = card.querySelector('[data-aa-series-product-count]');
      if (label) label.textContent = `${count} PRODUCTS`;
    });
  }

  syncHomeSeriesProductCounts().catch(() => {});
})();

(() => {
  const root = document.querySelector('[data-aa-series-directory]');
  if (!root) return;

  const pageSize = Number(root.dataset.aaPageSize || 8);
  const statusLabel = root.dataset.aaStatusLabel || 'Series';
  const tabs = [...root.querySelectorAll('[data-aa-directory-tabs] [data-category]')];
  const firstTab = tabs[0];
  const search = root.querySelector('[data-aa-directory-search]');
  const searchClear = root.querySelector('[data-aa-directory-search-clear]');
  const countValue = root.querySelector('[data-aa-directory-count-value]');
  const empty = root.querySelector('[data-aa-series-empty]');
  const paginationRoot = root.querySelector('[data-aa-series-pagination]');
  const state = {
    query: '',
    category: firstTab?.dataset.category || 'ALL',
    match: (firstTab?.dataset.match || 'ALL').toUpperCase(),
    page: 1,
  };

  function cards() {
    return [...root.querySelectorAll('[data-aa-series-card]')];
  }

  async function syncSeriesProductCounts() {
    const feed = root.dataset.aaProductFeedUrl || '';
    const pages = Number(root.dataset.aaProductFeedPages || 0);
    if (!feed || !pages) return;
    const counts = new Map();
    for (let page = 1; page <= pages; page += 1) {
      const join = feed.includes('?') ? '&' : '?';
      let response;
      try {
        response = await fetch(`${feed}${join}page=${page}`, { credentials: 'same-origin' });
      } catch (err) {
        break;
      }
      if (!response?.ok) break;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const products = [...doc.querySelectorAll('[data-aa-product]')];
      if (!products.length) break;
      products.forEach((product) => {
        const handles = new Set(
          String(product.dataset.seriesHandles || '')
            .toLowerCase()
            .split('|')
            .map((handle) => handle.trim())
            .filter(Boolean),
        );
        handles.forEach((handle) => counts.set(handle, (counts.get(handle) || 0) + 1));
      });
    }
    document.querySelectorAll('[data-aa-series-card][data-series-handle]').forEach((card) => {
      const handle = String(card.dataset.seriesHandle || '').toLowerCase();
      const count = counts.get(handle) || 0;
      card.dataset.count = String(count);
      const label = card.querySelector('[data-aa-series-product-count]');
      if (label) label.textContent = `${count} PRODUCTS`;
    });
  }

  function pageItems(page, totalPages) {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const items = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) items.push('…');
    for (let value = start; value <= end; value += 1) items.push(value);
    if (end < totalPages - 1) items.push('…');
    items.push(totalPages);
    return items;
  }

  function cardMatches(card) {
    const query = state.query.trim().toLowerCase();
    const haystack = (card.dataset.search || card.dataset.title || '').toLowerCase();
    if (query && !haystack.includes(query)) return false;
    if (state.category.toUpperCase() !== 'ALL' && state.match !== 'ALL') {
      if ((card.dataset.type || '').toUpperCase() !== state.match.toUpperCase()) return false;
    }
    return true;
  }

  function syncChrome(matchedCount) {
    if (countValue) countValue.textContent = String(matchedCount);
    tabs.forEach((tab) => {
      tab.classList.toggle('active', tab.dataset.category === state.category);
    });
    if (empty) empty.hidden = matchedCount > 0;
  }

  function renderPagination(page, totalPages, matchedCount) {
    if (!paginationRoot) return;
    if (totalPages <= 1) {
      paginationRoot.innerHTML = '';
      return;
    }
    const start = matchedCount === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, matchedCount);
    const status = matchedCount ? `Showing ${start}–${end} of ${matchedCount} ${statusLabel}` : '';
    const prevDisabled = page === 1 ? 'disabled' : '';
    const nextDisabled = page === totalPages ? 'disabled' : '';
    const chevronLeft = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>';
    const chevronRight = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';
    paginationRoot.innerHTML = `<nav class="aa-pagination" aria-label="Series pages">${
      `<button type="button" data-page="${page - 1}" ${prevDisabled} aria-label="Previous page">${chevronLeft}</button>`
    }${pageItems(page, totalPages)
      .map((item) =>
        item === '…'
          ? '<span aria-hidden>…</span>'
          : `<button type="button" data-page-number="${item}" class="${item === page ? 'active' : ''}" ${item === page ? 'aria-current="page"' : ''}>${item}</button>`,
      )
      .join('')}${
      `<button type="button" data-page="${page + 1}" ${nextDisabled} aria-label="Next page">${chevronRight}</button>`
    }${status ? `<p class="aa-pagination-status">${status}</p>` : ''}</nav>`;
  }

  function render(scroll) {
    const matched = cards().filter(cardMatches);
    const totalPages = Math.max(1, Math.ceil(matched.length / pageSize));
    if (state.page > totalPages) state.page = totalPages;
    if (state.page < 1) state.page = 1;
    const start = (state.page - 1) * pageSize;
    const shown = new Set(matched.slice(start, start + pageSize));
    cards().forEach((card) => {
      card.hidden = !shown.has(card);
    });
    syncChrome(matched.length);
    renderPagination(state.page, matched.length === 0 ? 0 : totalPages, matched.length);
    if (scroll) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function resetAll() {
    state.query = '';
    state.page = 1;
    state.category = firstTab?.dataset.category || 'ALL';
    state.match = (firstTab?.dataset.match || 'ALL').toUpperCase();
    if (search) search.value = '';
    if (searchClear) searchClear.hidden = true;
    render(false);
  }

  root.addEventListener('input', (event) => {
    if (event.target !== search) return;
    state.query = search.value;
    if (searchClear) searchClear.hidden = !state.query;
    state.page = 1;
    render(false);
  });

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-aa-directory-search-clear]')) {
      state.query = '';
      if (search) search.value = '';
      if (searchClear) searchClear.hidden = true;
      state.page = 1;
      render(false);
      return;
    }
    const tab = event.target.closest('[data-aa-directory-tabs] [data-category]');
    if (tab) {
      state.category = tab.dataset.category || 'ALL';
      state.match = (tab.dataset.match || state.category).toUpperCase();
      state.page = 1;
      render(false);
      return;
    }
    if (event.target.closest('[data-aa-series-reset]')) {
      resetAll();
      return;
    }
    const pageBtn = event.target.closest('[data-aa-series-pagination] [data-page], [data-aa-series-pagination] [data-page-number]');
    if (pageBtn && !pageBtn.disabled) {
      const next = Number(pageBtn.dataset.pageNumber || pageBtn.dataset.page);
      if (next >= 1) {
        state.page = next;
        render(true);
      }
    }
  });

  render(false);
  syncSeriesProductCounts().catch(() => {});
})();

(() => {
  function gateScope(node) {
    const gate = node?.closest?.('[data-aa-preorder-gate]');
    return gate?.parentElement || node?.closest?.('.cart__ctas')?.parentElement || document;
  }

  function syncPreorderGate(scope) {
    const root = scope || document;
    const box = root.querySelector('[data-aa-preorder-confirm]');
    if (!box) return;
    const ok = box.checked;
    root.querySelectorAll('[data-aa-preorder-checkout]').forEach((btn) => {
      btn.disabled = !ok;
    });
    root.querySelectorAll('[data-aa-preorder-express]').forEach((el) => {
      el.classList.toggle('is-preorder-locked', !ok);
    });
    const error = root.querySelector('[data-aa-preorder-error]');
    if (error && ok) error.hidden = true;
  }

  document.addEventListener('change', (event) => {
    const box = event.target.closest('[data-aa-preorder-confirm]');
    if (!box) return;
    syncPreorderGate(gateScope(box));
  });

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-aa-preorder-checkout]');
    if (!btn) return;
    const root = gateScope(btn);
    const box = root.querySelector('[data-aa-preorder-confirm]');
    if (!box || box.checked) return;
    event.preventDefault();
    event.stopPropagation();
    const error = root.querySelector('[data-aa-preorder-error]');
    if (error) error.hidden = false;
    box.focus();
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== 'cart-form') return;
    const root = event.submitter ? gateScope(event.submitter) : form;
    const box = root.querySelector?.('[data-aa-preorder-confirm]');
    if (!box || box.checked) return;
    event.preventDefault();
    const error = root.querySelector('[data-aa-preorder-error]');
    if (error) error.hidden = false;
    box.focus();
  }, true);
})();
